/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Chip } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { makeStyles } from 'tss-react/mui';
import {
  IApplicationDetails,
  IPlanDetailsProductsCounter,
  initValuesPlanDetailsProductsCounter,
  initialValues,
} from '.';
import EnhancedButton from '@components/_old/EnhancedButton';
import Loader from '@components/loader/Loader';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import EnhancedStepper from '@components/stepper/EnhancedStepper';
import Separator from '@components/common/Separator';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import QuotesCards, {
  quote,
} from '@components/widgets/dynamic-cardview/QuotesCards';
import EnhancedChipInput from '@components/_old/form/EnhancedChipInput';
import EnhancedCurrencyInput from '@components/_old/form/EnhancedCurrencyInput';
import EnhancedInput from '@components/_old/form/EnhancedInput';
import EnhancedInternationalPhoneInput from '@components/_old/form/EnhancedInternationalPhoneInput';
import ShowForUser from '@components/common/ShowForUser';
import { IEntityInfoView } from '@components/widgets/entity-info/EntityInfoFields';
import EntityInfoWidget from '@components/widgets/entity-info/EntityInfoWidget';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import StaticLayout from '@components/layout/StaticLayout';
import { useAppSelector } from '@redux/hooks';
import DataService from '@services/DataService';
import { capitalizeFirstLetter } from '@utils/formatting-utils';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import { isEmpty } from '@utils/validationUtils';
import { entityView, steps } from './content';
import {
  acceptQuotes,
  calculateAction,
  validateVignette,
  checkPlateNumberHasActiveProductPolicy,
  downloadQuote,
  getApplicationDetails,
  getModelsByBrands,
  issuePolicy,
  previewPolicies,
  sendQuotes,
  getCopyPolicyDetailsQuery,
  getPolicyByNumber,
  checkApplicationHasActiveMBI,
} from './queries';
import {
  adjustAgencyRepair,
  checkEmail,
  checkPhoneNumber,
  checkQuoteName,
  entityToApplicationDetails,
  extractLovs,
  filterBodyTypeLOVs,
  filterProductsByAgentProductsLOVS,
  filterBreakdownServicesLOVs,
  filterTowingServiceLOVs,
  filterUsageTypeLOVs,
  getApplicationProgress,
  getApplicationStatus,
  graphqlEntityToQuoteDetails,
  isCashPlusAgency,
  isVisibleBreakdownServicesField,
  isVisiblePlanDetailsField,
  logMotorBreakdownDebug,
  logMotorDebug,
  summarizePlanDetailsForLog,
  DEFAULT_CAR_NATIONALITY,
} from './utils';
import ApplicationCopyPolicyDetailsDrawer from '../application-copy-policy-details-drawer/ApplicationCopyPolicyDetailsDrawer';
import ApplicationDrawer from '../../application-drawer/ApplicationDrawer';
import { createActionAsAgent } from '../applications-listing/queries';
import ApplicationDrawerWithMultipleAgencies from '../../application-with-multiple-agencies-drawer/ApplicationDrawerWithMultipleAgencies';
import DateService from '@services/DateService';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import { CDN_URL, CURRENCY_SYMBOL } from '@utils';
import EnhancedNumberInput from '@components/_old/form/EnhancedNumberInput';
import dayjs from 'dayjs';

const useStyles = makeStyles()(() => ({
  stepper: {
    backgroundColor: 'transparent',
    maxWidth: '90%',
    marginBottom: '20px',
    '& .MuiStepLabel-root .Mui-active, .MuiStepLabel-root .Mui-completed': {
      color: MAIN_THEME.color.black,
    },

    '@media only screen and (min-width: 600px)': {
      maxWidth: '1300px',
    },
  },
  section: {
    display: 'grid',
    'grid-template-columns': '19.5% 19.5% 19.5% 19.5% 19.5%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',

    '& label': {
      '& span': {
        margin: '-2px 0',
      },
    },
  },
  sectionTitle: {
    padding: '-10x 10x!important',
  },
  sectionFullRow: {
    display: 'grid',
    'grid-template-columns': '100%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  row: {
    flexWrap: 'wrap',
    margin: '15px 20px 10px 0px',
    alignItems: 'flex-start',
    display: 'grid',
    gap: '5px',
    '& label': {
      flexGrow: '1',
      flexBasis: '0',
      minWidth: '0',
    },
    'grid-template-columns': '19.5% 19.5% 19.5% 19% 16% 3% 3%',
  },
  thickSeperator: {
    height: 10,
    margin: '10px 0 10px 0',
    'grid-column-start': '1',
    'grid-column-end': '4',
  },
  field: {
    width: '96%',
    marginRight: '10px !important',
  },
  repeaterField: {
    margin: '0px!important',
  },
  breakdownServicesField: {
    width: '96%',
    marginRight: '10px !important',
    gridColumn: 'span 2',
  },
  breakdownServicesLabel: {
    margin: '0px!important',
    whiteSpace: 'nowrap',
  },
  arField: {
    width: '96%',
    marginRight: '10px !important',
    direction: 'rtl',
  },
  header: {
    fontFamily: MAIN_THEME.font.bold,
    fontSize: MAIN_THEME.fontSize.rg,
    color: MAIN_THEME.color.black,
    '&:hover': {
      color: MAIN_THEME.color.black,
    },
  },
  content: {
    padding: '0',
  },
  dirtyChip: {
    margin: '0 0 -15px 0',
    backgroundColor: MAIN_THEME.color.black,
    '& span': {
      color: MAIN_THEME.color.white,
    },
  },
}));

const ApplicationDetailsPageMotor: React.FC = () => {
  //#region react and redux hooks
  const { classes } = useStyles();
  const params = useParams();
  const navigate = useNavigate();
  //#endregion

  //#region component hooks and states
  const [applicationInfo, setApplicationInfo] =
    useState<IApplicationDetails>(initialValues);

  const [updatedLeftSideInfo, setUpdatedLeftSideInfo] =
    useState<IEntityInfoView>(entityView);

  const [lovs, setLovs] =
    useState<Record<string, Record<string, string>>>(null);

  const [bodyTypes, setBodyTypes] = useState<Record<string, string>>(undefined);

  const [towingService, setTowingService] =
    useState<Record<string, string>>(undefined);

  const [breakdownServices, setBreakdownServices] =
    useState<Record<string, string>>(undefined);

  const [usageTypes, setUsageTypes] =
    useState<Record<string, string>>(undefined);

  const [agencyRepairs, setAgencyRepairs] = useState<Record<string, string>[]>(
    []
  );

  const [loadingState, setLoadingState] = useState<boolean>(true);
  const [lob, setLob] = useState<string>('');

  const [loadingModelsState, setLoadingModelsState] = useState<boolean>(false);

  const [copyPolicyDetailsDrawerOpen, setCopyPolicyDetailsDrawerOpen] =
    useState<boolean>(false);

  const [loadingNameOnLicenseArState, setLoadingNameOnLicenseArState] =
    useState<boolean>(false);
  const [loadingNameOnLicenseEnState, setLoadingNameOnLicenseEnState] =
    useState<boolean>(false);
  const [planDetailsKeysStatus, setPlanDetailsKeysStatus] = useState<
    Record<string, boolean>[]
  >([{ coverageType: false }]);
  const [newButtonState, setNewButtonState] = useState<EnhancedButtonStatus>();

  const [newAppDrawerOpen, setNewAppDrawerOpen] = useState<boolean>(false);
  const [
    newApplicationWithMultipleAgencieDrawerOpen,
    setNewApplicationWithMultipleAgencieDrawerOpen,
  ] = useState<boolean>(false);

  const countryCode = useRef<string>();
  const displayDirtyMessage = useRef<number>(0);
  const dirtyChange = useRef<number>(0);
  const originalRenewalProduct = useRef<string | null>(null);
  const nameOnLicenseArTransliterationController =
    useRef<AbortController | null>(null);

  const [isUnderwriter, setIsUnderwriter] = useState<boolean>(false);
  const [isIssuingAgent, setIsIssuingAgent] = useState<boolean>(false);
  const [isQuotingAgent, setIsQuotingAgent] = useState<boolean>(false);

  const [agentType, setAgentType] = useState<string>('');
  const [hasMBI, setHasMBI] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { isDirty, errors, dirtyFields },
    watch,
  } = useForm<IApplicationDetails>({
    defaultValues: initialValues,
    shouldUnregister: true,
  });

  const disabledSendBtn = isDirty;
  const disabledAcceptBtn = isDirty;
  const disabledIssuePolicyBtn = isDirty;
  dirtyChange.current += 1;
  displayDirtyMessage.current =
    dirtyChange.current > 4 ? 1 : displayDirtyMessage.current;

  useEffect(() => {
    if (!isDirty) {
      displayDirtyMessage.current = 0;
    }
    logMotorDebug('dirty', 'formState changed', {
      isDirty,
      dirtyFields,
      displayDirtyMessage: displayDirtyMessage.current,
      dirtyChange: dirtyChange.current,
      planDetails: summarizePlanDetailsForLog(
        getValues('planDetails'),
        lovs?.products
      ),
      errorKeys: Object.keys(errors),
    });
  }, [isDirty, dirtyFields, errors]);

  const planDetailsFieldArray = useFieldArray({
    control,
    name: 'planDetails',
  });

  const { replace } = useFieldArray({
    control,
    name: 'planDetails',
  });

  const watchedPlanDetails = watch('planDetails', initialValues['planDetails']);
  let quoteName = '';

  const updatedPlanDetails: any[] = null;

  let planDetailsProducts: any[] = null;
  const prevPlanDetailsProducts: React.MutableRefObject<any[]> =
    useRef(planDetailsProducts);

  const handlePlanDetailsChange = () => {
    if (
      planDetailsProducts &&
      typeof planDetailsProducts === 'object' &&
      Object.keys(planDetailsProducts).length > 0 &&
      (prevPlanDetailsProducts.current === null ||
        (prevPlanDetailsProducts.current != null &&
          (Object.keys(planDetailsProducts).length !==
            Object.keys(prevPlanDetailsProducts.current).length ||
            Object.keys(prevPlanDetailsProducts.current).some(
              (product, index) => product !== planDetailsProducts[index]
            ))))
    ) {
      let productsCounts: Record<string, number> = {};
      let unknownProductsCount = 0;

      if (lovs && planDetailsFieldArray) {
        const newFieldArrayValues = planDetailsFieldArray.fields.map((f) => {
          if (f.product) {
            productsCounts = {
              ...productsCounts,
              [f.product]: (productsCounts[f.product] || 0) + 1,
            };
          } else {
            unknownProductsCount += 1;
          }
          const productsCountSum = Object.values(productsCounts).reduce(
            (acc, curr) => acc + curr,
            0
          );
          if (f.product) {
            const productKey = Object.keys(lovs.products).find(
              (p) => p === f.product
            );
            const productLabel = lovs.products[productKey];
            quoteName = `${productLabel} ${productsCounts[f.product]}`;
          } else {
            quoteName = `Quote ${productsCountSum + unknownProductsCount}`;
          }
          return {
            ...f,
            name: quoteName,
            id: undefined as any,
          };
        });
        logMotorDebug('dirty', 'handlePlanDetailsChange – replace field array', {
          products: planDetailsProducts,
          newFieldArrayValues: newFieldArrayValues.map((f) => ({
            product: f.product,
            name: f.name,
          })),
        });
        replace(newFieldArrayValues);
      }
      prevPlanDetailsProducts.current = planDetailsProducts;
    }
  };

  const [planDetailsProductCounter, setPlanDetailsProductCounter] =
    useState<IPlanDetailsProductsCounter>(initValuesPlanDetailsProductsCounter);

  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [formDisabled, setFormDisabled] = useState(false);

  const user = useAppSelector((state) => state.user);
  const entityId = params.id;
  //#endregion
  const userAgentType = user.info.agentType;

  //#region graphql hooks
  const [getApplicationDetailsLazy, entityResult] = useLazyQuery(
    getApplicationDetails(),
    {
      variables: { id: entityId },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    }
  );

  const [acceptAction] = useMutation(acceptQuotes(), {
    refetchQueries: [getApplicationDetails()],
  });

  const [sendQuoteAction] = useMutation(sendQuotes(), {
    refetchQueries: [getApplicationDetails()],
  });

  const [issuePolicyAction] = useMutation(issuePolicy(), {
    refetchQueries: [getApplicationDetails()],
  });

  const [downloadQuoteAction] = useMutation(downloadQuote(), {
    refetchQueries: [getApplicationDetails()],
  });

  const [previewPoliciesAction] = useMutation(previewPolicies(), {
    refetchQueries: [getApplicationDetails()],
  });

  const [checkPlateNumberHasActiveProductPolicyLazy] = useLazyQuery(
    checkPlateNumberHasActiveProductPolicy(),
    {}
  );

  const [getModelsByBrandsLazy, modelByBrandsQueryRes] =
    useLazyQuery(getModelsByBrands());

  const [copyPolicyDetailsLazy] = useLazyQuery(getCopyPolicyDetailsQuery(), {});

  const [validateVignetteLazy] = useMutation(validateVignette(), {});

  const [mutateFunction] = useMutation(calculateAction(), {
    refetchQueries: [getApplicationDetails()],
    errorPolicy: 'all',
  });

  const [createAppMutateFunction] = useMutation(createActionAsAgent(), {
    refetchQueries: [getApplicationDetails()],
    awaitRefetchQueries: true,
    errorPolicy: 'all',
  });

  const [getPolicyByNumberLazy, resultData] = useLazyQuery(
    getPolicyByNumber(),
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [checkApplicationHasMBILazy] = useLazyQuery(
    checkApplicationHasActiveMBI(),
    {
      fetchPolicy: 'no-cache',
    }
  );

  const getPlanDetailsProductsCounter = (planDetails: any) => {
    if (planDetails !== null) {
      let newPlandetailsProductCounter = { ...planDetailsProductCounter };
      planDetails.forEach((planDetail: any) => {
        newPlandetailsProductCounter = {
          ...newPlandetailsProductCounter,
          [planDetail['product']]:
            newPlandetailsProductCounter[
              planDetail[
                'product'
              ] as keyof typeof initValuesPlanDetailsProductsCounter
            ] + 1,
        };
        setPlanDetailsProductCounter(newPlandetailsProductCounter);
      });
    }
  };

  const isClosed =
    entityResult?.data?.Insurance?.entities?.application?.views?.Insurance_all?.properties?.Status.toLowerCase() ===
    'closed';
  const isNew =
    entityResult?.data?.Insurance?.entities?.application?.views?.Insurance_all?.properties?.Status.toLowerCase() ===
    'new';

  const showDirtyDisclaimerChip =
    isDirty &&
    displayDirtyMessage.current > 0 &&
    !isNew &&
    !isClosed;

  useEffect(() => {
    logMotorDebug('dirty', 'disclaimer chip visibility', {
      showDirtyDisclaimerChip,
      isDirty,
      displayDirtyMessage: displayDirtyMessage.current,
      dirtyChange: dirtyChange.current,
      isNew,
      isClosed,
    });
  }, [showDirtyDisclaimerChip, isDirty, isNew, isClosed]);
  const isEnabled =
    entityResult?.data?.Insurance?.entities?.application?.actions
      ?.able_To_SimulateAndSaveMotor;
  const buttonLabel = 'Calculate';
  const buttonType = Object.keys(errors).length === 0 ? 'submit' : 'button';

  //#endregion

  //#region useEffect

  useEffect(() => {
    reset({
      ...initialValues,
    });
    getApplicationDetailsLazy();
  }, [entityId]);

  const isFirstLoad = useRef(true);

  useEffect(() => {
    try {
      if (entityResult.loading || !entityResult.data) return;
      logMotorDebug('data-load', 'entityResult effect – data arrived', {
        isFirstLoad: isFirstLoad.current,
        isDirtyBeforeLoad: isDirty,
        status:
          entityResult.data?.Insurance?.entities?.application?.views
            ?.Insurance_all?.properties?.Status,
      });
      dirtyChange.current = 0;
      setLoadingState(false);

      const extractedLovs = extractLovs(entityResult.data);
      if (
        user.userRoles.includes('Insurance-QuotingAgent') ||
        user.userRoles.includes('Insurance-IssuingAgent')
      ) {
        extractedLovs.products = filterProductsByAgentProductsLOVS(
          extractedLovs.products,
          user.info.products
        );
      }
      setLovs(extractedLovs);

      const applicationDetails = entityToApplicationDetails(entityResult.data);
      if (!applicationDetails.expiryDate) {
        applicationDetails.expiryDate = DateService.addYears(new Date(), 1);
      }
      if (!applicationDetails.planDetails?.length) {
        applicationDetails.planDetails = initialValues.planDetails;
      }

      const applicationProperties = entityView.sections[0];
      applicationProperties.properties.stateReason.value =
        capitalizeFirstLetter(
          applicationDetails.statusReason.replaceAll('_', ' ')
        );
      applicationProperties.properties.lineOfBusiness.value =
        applicationDetails.lineOfBusiness;
      applicationProperties.properties.agency.value =
        applicationDetails.agencyName;
      applicationProperties.properties.agentName.value =
        applicationDetails.agentName;
      applicationProperties.properties.customerName.value =
        applicationDetails.customerName;

      const systemProperties = entityView.sections[1];
      systemProperties.properties.createdBy.value =
        applicationDetails.createdBy;
      systemProperties.properties.createdOn.value =
        applicationDetails.createdOn;
      systemProperties.properties.modifiedBy.value =
        applicationDetails.modifiedBy;
      systemProperties.properties.modifiedOn.value =
        applicationDetails.modifiedOn;

      setApplicationInfo(applicationDetails);

      setLob(applicationDetails.lineOfBusiness);
      setUpdatedLeftSideInfo({
        ...entityView,
        sections: [applicationProperties, systemProperties],
      });

      if (applicationDetails.planDetails.length) {
        getPlanDetailsProductsCounter(applicationDetails.planDetails);
      }
      const newKeys = cloneDeep(planDetailsKeysStatus);
      const newAgencyRepairs: Record<string, string>[] = [];
      applicationDetails.planDetails.forEach((pd: any, i: any) => {
        newKeys[i] = { coverageType: !isEmpty(pd.includeAgencyRepair) };
        newAgencyRepairs.push(
          adjustAgencyRepair(pd.carValue as number, extractedLovs.agencyRepairs)
        );
      });
      setPlanDetailsKeysStatus(newKeys);
      setAgencyRepairs(newAgencyRepairs);

      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        reset({
          ...applicationDetails,
          mobileNumber: applicationDetails.mobileNumber ?? countryCode.current,
        });
        logMotorDebug('data-load', 'initial reset from API', {
          planDetails: summarizePlanDetailsForLog(
            applicationDetails.planDetails,
            extractedLovs.products
          ),
        });
      }

      const usageType = getValues('usageType') || applicationDetails.usageType;
      const bodyType = getValues('bodyType') || applicationDetails.bodyType;
      const plateCode = getValues('plateCode') || applicationDetails.plateCode;
      const carNationality =
        getValues('carNationality') || applicationDetails.carNationality;

      setBodyTypes(
        filterBodyTypeLOVs(
          usageType,
          extractedLovs.bodyTypes,
          carNationality
        )
      );
      if (!isEmpty(bodyType)) {
        const weight =
          getValues('weight') || applicationDetails.weight;
        const model = getValues('model') || applicationDetails.model;
        const brand = getValues('brand') || applicationDetails.brand;
        setTowingService(
          filterTowingServiceLOVs(
            bodyType,
            extractedLovs.towingServices,
            weight
          )
        );
        setBreakdownServices(
          filterBreakdownServicesLOVs(
            bodyType,
            extractedLovs.breakdownServices,
            weight,
            model,
            brand
          )
        );
        logMotorDebug('field-reset', 'post-load – review breakdown/towing', {
          bodyType,
          weight,
          planDetails: summarizePlanDetailsForLog(
            getValues('planDetails'),
            extractedLovs.products
          ),
        });
        checkBreakdownServicesValue(
          filterBreakdownServicesLOVs(
            bodyType,
            extractedLovs.breakdownServices,
            weight,
            model,
            brand
          )
        );
      }
      setUsageTypes(
        filterUsageTypeLOVs(plateCode, extractedLovs.usageTypes, carNationality)
      );

      if (applicationDetails.brand) {
        getModelsByBrandsLazy({
          variables: { modelId: applicationDetails.brand },
        });
      }

      const isUW = user.userRoles.includes('Insurance-Underwriter');
      const isQA = user.userRoles.includes('Insurance-QuotingAgent');
      const isIA = user.userRoles.includes('Insurance-IssuingAgent');
      setIsUnderwriter(isUW);
      setIsQuotingAgent(isQA);
      setIsIssuingAgent(isIA);
      setAgentType(isUW ? 'underwriter' : isQA || isIA ? 'agent' : '');

      const status =
        entityResult.data.Insurance.entities.application.views.Insurance_all.properties.Status.toLowerCase();
      setFormDisabled(status === 'closed' || !(isUW || isQA || isIA));
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  }, [entityResult.loading, entityResult.data, reset]);

  useEffect(() => {
    if (modelByBrandsQueryRes.loading) setLoadingModelsState(true);
    if (modelByBrandsQueryRes.error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
    if (modelByBrandsQueryRes.data) {
      const updatedDropDownLists = cloneDeep(lovs);
      updatedDropDownLists.models = lookupListAsRecordObject(
        modelByBrandsQueryRes.data?.Insurance?.lookups?.models
      );

      setLovs(updatedDropDownLists);
      setLoadingModelsState(false);
    }
  }, [modelByBrandsQueryRes]);

  const getProductCodeByTitle = (title: string) =>
    lovs
      ? Object.keys(lovs.products).find((k) => lovs.products[k] === title)
      : undefined;

  const computeHasMBI = async (dataOrForm?: IApplicationDetails) => {
    // pull the latest values either from form or from provided data
    const cur = dataOrForm ?? getValues();

    if (!lovs) {
      logMotorDebug('mbi', 'computeHasMBI – no lovs, returning false', {});
      return false;
    }

    const MTP_CODE = getProductCodeByTitle('Motor Third Party');
    const MBI_CODE = getProductCodeByTitle('Motor Bodily Injury');

    const selected = (cur?.planDetails || [])
      .map((p: any) => p?.product)
      .filter(Boolean) as string[];

    // Branch #1: both are selected now  -> True (no query)
    const hasBoth =
      MTP_CODE &&
      MBI_CODE &&
      selected.includes(MTP_CODE) &&
      selected.includes(MBI_CODE);

    if (hasBoth) {
      logMotorDebug('mbi', 'computeHasMBI – branch: both MTP+MBI selected', {
        MTP_CODE,
        MBI_CODE,
        selected,
        result: true,
      });
      return true;
    }

    // Branch #2: only MTP is selected -> use your query to ask the server
    const onlyMTP =
      MTP_CODE &&
      selected.includes(MTP_CODE) &&
      (!MBI_CODE || !selected.includes(MBI_CODE));

    if (
      onlyMTP &&
      cur?.chassis &&
      cur?.plateCode &&
      cur?.plateNumber &&
      cur?.motor
    ) {
      logMotorDebug('mbi', 'computeHasMBI – branch: only MTP, querying server', {
        MTP_CODE,
        MBI_CODE,
        selected,
        chassis: cur.chassis,
        plateCode: cur.plateCode,
        plateNumber: cur.plateNumber,
        motor: cur.motor,
      });
      const { data } = await checkApplicationHasMBILazy({
        variables: {
          // NOTE: schema param is spelled "chasis"
          chasis: cur.chassis,
          plateCode: cur.plateCode,
          plateNumber: `${cur.plateNumber ?? ''}`,
          motor: cur.motor,
          effectiveDate: cur.effectiveDate,
        },
      });
      const anyRow =
        (data?.Insurance?.queries?.checkApplicationHasActiveMBI?.length ?? 0) >
        0;
      logMotorDebug('mbi', 'computeHasMBI – server query result', {
        activeMbiCount:
          data?.Insurance?.queries?.checkApplicationHasActiveMBI?.length ?? 0,
        result: anyRow,
      });
      return anyRow;
    }

    logMotorDebug('mbi', 'computeHasMBI – branch: default false', {
      MTP_CODE,
      MBI_CODE,
      selected,
      onlyMTP,
      onlyMBI: MBI_CODE && selected.includes(MBI_CODE),
      hasVehicleIds: Boolean(
        cur?.chassis && cur?.plateCode && cur?.plateNumber && cur?.motor
      ),
      result: false,
    });
    // Otherwise -> False
    return false;
  };

  //#endregion

  //#region functions

  const onSubmit: SubmitHandler<IApplicationDetails> = async (data) => {
    logMotorDebug('calculate', 'onSubmit invoked', {
      isEnabled,
      buttonLabel,
      isClosed,
      buttonType,
      isDirty,
      errorKeys: Object.keys(errors),
      planDetails: summarizePlanDetailsForLog(data.planDetails, lovs?.products),
    });
    if (!isEnabled || buttonLabel !== 'Calculate' || isClosed) {
      logMotorDebug('calculate', 'onSubmit blocked by guard', {
        isEnabled,
        buttonLabel,
        isClosed,
      });
      return;
    }
    setFormDisabled(true);
    setSubmitButtonState('loading');

    data.planDetails.forEach((item, index) => {
      if (!Object.prototype.hasOwnProperty.call(item, 'name')) {
        item.name = planDetailsFieldArray.fields[index].name;
        item.product = planDetailsFieldArray.fields[index].product;
      }
      if ('id' in item) {
        delete item['id'];
      }
    });

    const planInputs = data.planDetails.map((planInput) => {
      const copy = { ...planInput };
      copy.carValue = Number(copy.carValue);
      copy.towingService = isVisiblePlanDetailsField(
        copy.product,
        'towingService'
      )
        ? copy.towingService
        : null;
      copy.breakdownServices = isVisibleBreakdownServicesField(
        copy.product,
        data.bodyType,
        data.weight,
        data.model,
        data.brand
      )
        ? copy.breakdownServices
        : null;
      copy.vignetteCode = isVisiblePlanDetailsField(copy.product, 'vignette')
        ? copy.vignetteCode
        : null;
      copy.vignette = isVisiblePlanDetailsField(copy.product, 'vignette')
        ? Number(copy.vignette)
        : null;
      copy.coverageType = isVisiblePlanDetailsField(
        copy.product,
        'coverageType'
      )
        ? copy.coverageType
        : null;
      copy.includeAgencyRepair = isVisiblePlanDetailsField(
        copy.product,
        'includeAgencyRepair'
      )
        ? copy.includeAgencyRepair
        : null;
      return copy;
    });

    const resolvedHasMBI = await computeHasMBI(data);
    setHasMBI(resolvedHasMBI);

    logMotorDebug('calculate', 'mutate – sending calculate', {
      resolvedHasMBI,
      planInputs: planInputs.map((p) => ({
        product: p.product,
        productTitle: lovs?.products?.[p.product as string],
        towingService: p.towingService,
        breakdownServices: p.breakdownServices,
        vignetteCode: p.vignetteCode,
        vignette: p.vignette,
      })),
    });

    mutateFunction({
      variables: {
        entityId,
        effectiveDate: DateService.formatDateBackend(data.effectiveDate),
        isRenewal: data.isRenewal,
        renewalNumber: data.renewalNumber,
        vehicleInputs: {
          // Persisted on Calculate (Issue Policy only sends quote IDs).
          // Field is registered only for Cash Plus (shouldUnregister).
          ...(!isEmpty(data.carNationality)
            ? { carNationality: data.carNationality }
            : {}),
          plateCode: !isEmpty(data.plateCode) ? data.plateCode : undefined,
          plateNumber: !isEmpty(data.plateNumber)
            ? `${data.plateNumber}`
            : undefined,
          usageType: data.usageType,
          bodyType: data.bodyType,
          engineType: data.engineType,
          brand: data.brand || null,
          model: data.model || null,
          yearOfMake: parseInt(data.yearOfMake as unknown as string),
          motor: data.motor,
          weight: parseFloat(data.weight as unknown as string),
          engineSize: parseFloat(data.engineSize as unknown as string),
          seats: parseInt(data.seats as unknown as string),
          chassis: data.chassis,
          horsePower: parseFloat(data.horsePower as unknown as string),
          nameOnLicenseEn: data.nameOnLicenseEn,
          nameOnLicenseAr: data.nameOnLicenseAr,
        },
        applicationContactInputs: {
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          fullName: `${data.firstName} ${data.middleName} ${data.lastName}`,
          email: data.email || null,
          mobileNumber:
            data.mobileNumber && data.mobileNumber.length > 3
              ? data.mobileNumber.startsWith('+')
                ? data.mobileNumber
                : `+${data.mobileNumber}`
              : undefined,
          country: data.country,
          city: data.city,
          street: data.street || null,
          building: data.building || null,
          title: data.title,
          phoneType: data.phoneType,
        },
        plansInputs: planInputs,
        hasMBI: resolvedHasMBI,
      },
    })
      .then((res) => {
        if (isEmpty(res.errors)) {
          logMotorBreakdownDebug('calculate success – submitted planInputs', {
            planInputs: planInputs.map((p) => ({
              product: p.product,
              breakdownServices: p.breakdownServices,
            })),
          });
          logMotorDebug('calculate', 'mutate success – before reset', {
            isDirtyBeforeReset: isDirty,
            displayDirtyMessage: displayDirtyMessage.current,
          });
          setSubmitButtonState('success');
          toast.success(<ToastSuccessMessage>Success</ToastSuccessMessage>);
          reset(data);
          displayDirtyMessage.current = 0;
          dirtyChange.current = 0;
          logMotorDebug('calculate', 'mutate success – after reset+replace', {
            displayDirtyMessage: displayDirtyMessage.current,
            planDetails: summarizePlanDetailsForLog(
              data.planDetails,
              lovs?.products
            ),
          });
          setTimeout(() => {
            logMotorDebug('calculate', 'mutate success – dirty state after refetch tick', {
              isDirty,
              dirtyFields,
              displayDirtyMessage: displayDirtyMessage.current,
              dirtyChange: dirtyChange.current,
              planDetails: summarizePlanDetailsForLog(
                getValues('planDetails'),
                lovs?.products
              ),
            });
          }, 500);
        } else {
          logMotorDebug('calculate', 'mutate returned errors', {
            errors: res.errors,
            graphQLError: getError(res),
          });
          setSubmitButtonState(undefined);
          toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        }
      })
      .catch((err) => {
        logMotorDebug('calculate', 'mutate threw', { error: String(err) });
        setSubmitButtonState(undefined);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      })
      .finally(() => {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
      });
  };
  const checkSubmission = () => {
    if (isEnabled && isClosed) {
      if ((isIssuingAgent || isQuotingAgent) && !isUnderwriter) {
        user.info.agencyId.length === 0 || user.info.agencyId.length === 1
          ? submitFormAsAgent()
          : setNewApplicationWithMultipleAgencieDrawerOpen(true);

        setNewButtonState(undefined);
      } else if (isUnderwriter && !(isIssuingAgent || isQuotingAgent)) {
        user.info.agencyId.length === 0 || user.info.agencyId.length === 1
          ? setNewAppDrawerOpen(true)
          : setNewApplicationWithMultipleAgencieDrawerOpen(true);

        setNewButtonState(undefined);
      }
    }

    reset({
      ...initialValues,
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      mobileNumber: '',
      city: '',
      street: '',
      building: '',
    });

    resetPartialForm();

    reset(initialValues);
    setValue('title', '');
    setValue('firstName', '');
    setValue('middleName', '');
    setValue('lastName', '');
    setValue('email', '');
    setValue('mobileNumber', '');
    setValue('city', '');
    setValue('street', '');
    setValue('building', '');

    setValue('yearOfMake', initialValues.yearOfMake);
    setValue('seats', initialValues.seats);
    setValue('horsePower', initialValues.horsePower);
  };

  const resetPartialForm = () => {
    reset({
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      mobileNumber: '',
      city: '',
      street: '',
      building: '',

      yearOfMake: initialValues.yearOfMake,
      seats: initialValues.seats,
      horsePower: initialValues.horsePower,
    });
  };

  const submitFormAsAgent = async () => {
    try {
      const { data, errors } = await createAppMutateFunction({
        variables: { lineOfBusiness: 'Motor' },
      });
      if (isEmpty(errors)) {
        setNewButtonState('success');
        toast.success(
          <ToastSuccessMessage>
            {'Application created successfully'}
          </ToastSuccessMessage>
        );
        const newApplicationId =
          data.insurance.actions.createApplicationAsAgent.id;
        setTimeout(() => {
          navigate('/sales/applications/motor/' + newApplicationId);
        }, 500);
      } else {
        toast.error(<ToastErrorMessage>{getError(data)}</ToastErrorMessage>);
      }
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setNewButtonState(undefined);
    }
  };

  const clearInapplicablePlanDetailFields = (
    rowIndex: number,
    product: string
  ) => {
    const bodyType = getValues('bodyType');
    const weight = getValues('weight');
    const model = getValues('model');
    const brand = getValues('brand');
    const hiddenFields: Array<{
      field: string;
      visible: boolean;
      emptyValue: string | undefined;
    }> = [
      {
        field: 'towingService',
        visible: isVisiblePlanDetailsField(product, 'towingService'),
        emptyValue: '',
      },
      {
        field: 'breakdownServices',
        visible: isVisibleBreakdownServicesField(
          product,
          bodyType,
          weight,
          model,
          brand
        ),
        emptyValue: '',
      },
      {
        field: 'vignetteCode',
        visible: isVisiblePlanDetailsField(product, 'vignette'),
        emptyValue: '',
      },
      {
        field: 'vignette',
        visible: isVisiblePlanDetailsField(product, 'vignette'),
        emptyValue: undefined,
      },
      {
        field: 'coverageType',
        visible: isVisiblePlanDetailsField(product, 'coverageType'),
        emptyValue: '',
      },
      {
        field: 'carValue',
        visible: isVisiblePlanDetailsField(product, 'carValue'),
        emptyValue: undefined,
      },
      {
        field: 'includeAgencyRepair',
        visible: isVisiblePlanDetailsField(product, 'includeAgencyRepair'),
        emptyValue: '',
      },
    ];

    hiddenFields.forEach(({ field, visible, emptyValue }) => {
      const path =
        `planDetails.${rowIndex}.${field}` as keyof typeof initialValues;
      if (visible) return;
      const planRow = getValues('planDetails')?.[rowIndex] as
        | Record<string, unknown>
        | undefined;
      const current = planRow?.[field];
      if (current !== '' && current != null && current !== undefined) {
        setValue(path, emptyValue as never, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      clearErrors(path);
    });
  };

  const checkTowingServiceValue = (towingLov: Record<string, string>) => {
    const list = getValues('planDetails') ?? [];

    list.forEach((pd: any, i: number) => {
      const needsTowing = isVisiblePlanDetailsField(
        pd?.product,
        'towingService'
      );
      const code = pd?.towingService;
      const fieldPath =
        `planDetails.${i}.towingService` as keyof typeof initialValues;

      if (!needsTowing) {
        if (code) {
          logMotorDebug(
            'field-reset',
            'checkTowingServiceValue – clearing hidden field',
            {
              index: i,
              product: pd?.product,
              productTitle: lovs?.products?.[pd?.product],
              shouldDirty: false,
            }
          );
          setValue(fieldPath, '' as never, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
        clearErrors(fieldPath);
        return;
      }

      if (!code || !towingLov[code]) {
        logMotorDebug('field-reset', 'checkTowingServiceValue – invalid LOV', {
          index: i,
          product: pd?.product,
          code,
          inLov: code ? Boolean(towingLov[code]) : false,
          shouldDirty: false,
        });
        setValue(fieldPath, '' as never, {
          shouldValidate: true,
          shouldDirty: false,
        });
      }
    });
  };

  const reviewTowingService = () => {
    if (!isEmpty(getValues('bodyType'))) {
      const list = filterTowingServiceLOVs(
        getValues('bodyType'),
        lovs.towingServices,
        getValues('weight')
      );
      setTowingService(list);
      checkTowingServiceValue(list);
    }
  };

  const checkBreakdownServicesValue = (
    breakdownLov?: Record<string, string>
  ) => {
    const list = getValues('planDetails') ?? [];
    const bodyType = getValues('bodyType');
    const weight = getValues('weight');
    const model = getValues('model');
    const brand = getValues('brand');
    const breakdownOptions = breakdownLov ?? lovs?.breakdownServices ?? {};

    list.forEach((pd: any, i: number) => {
      const needsBreakdown = isVisibleBreakdownServicesField(
        pd?.product,
        bodyType,
        weight,
        model,
        brand
      );
      const code = pd?.breakdownServices;
      const fieldPath =
        `planDetails.${i}.breakdownServices` as keyof typeof initialValues;

      if (!needsBreakdown) {
        if (code) {
          logMotorDebug(
            'field-reset',
            'checkBreakdownServicesValue – clearing hidden field',
            {
              index: i,
              product: pd?.product,
              productTitle: lovs?.products?.[pd?.product],
              shouldDirty: false,
            }
          );
          setValue(fieldPath, '' as never, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
        clearErrors(fieldPath);
        return;
      }

      if (!code || !breakdownOptions[code]) {
        logMotorDebug(
          'field-reset',
          'checkBreakdownServicesValue – invalid LOV',
          {
            index: i,
            product: pd?.product,
            code,
            inLov: code ? Boolean(breakdownOptions[code]) : false,
            shouldDirty: false,
          }
        );
        setValue(fieldPath, '' as never, {
          shouldValidate: true,
          shouldDirty: false,
        });
      }
    });
  };

  const reviewBreakdownServices = () => {
    if (!isEmpty(getValues('bodyType'))) {
      const list = filterBreakdownServicesLOVs(
        getValues('bodyType'),
        lovs.breakdownServices,
        getValues('weight'),
        getValues('model'),
        getValues('brand')
      );
      setBreakdownServices(list);
      checkBreakdownServicesValue(list);
    }
  };

  const autoSetNameOnLicenseAr = (enTxt: string) => {
    nameOnLicenseArTransliterationController.current?.abort();

    if (isEmpty(enTxt)) {
      setValue('nameOnLicenseAr', '');
      trigger('nameOnLicenseAr' as keyof typeof initialValues);
      return;
    }

    const controller = new AbortController();
    nameOnLicenseArTransliterationController.current = controller;

    setLoadingNameOnLicenseArState(true);
    DataService.transliterate(enTxt, controller)
      .then((result) => {
        setValue('nameOnLicenseAr', result);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          return;
        }

        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        setValue('nameOnLicenseAr', enTxt);
      })
      .finally(() => {
        if (nameOnLicenseArTransliterationController.current !== controller) {
          return;
        }

        nameOnLicenseArTransliterationController.current = null;
        setLoadingNameOnLicenseArState(false);
        trigger('nameOnLicenseAr' as keyof typeof initialValues);
      });
  };

  const autoSetNameOnLicenseEn = (fnameTxt?: string, lnameTxt?: string) => {
    setLoadingNameOnLicenseEnState(true);
    setValue('nameOnLicenseEn', `${fnameTxt} ${lnameTxt}`);
    setLoadingNameOnLicenseEnState(false);

    autoSetNameOnLicenseAr(`${fnameTxt} ${lnameTxt}`);
  };

  const validatePlateNumberOnProductChange = (
    product: string,
    lineIndex: number
  ) => {
    if (
      !isEmpty(getValues('chassis')) &&
      !isEmpty(getValues('effectiveDate')) &&
      !isEmpty(product) &&
      !isEmpty(getValues('motor'))
    ) {
      checkPlateNumberHasActiveProductPolicyLazy({
        variables: {
          plateCode: !isEmpty(getValues('plateCode'))
            ? (getValues('plateCode') as unknown as string)
            : undefined,
          plateNumber: !isEmpty(getValues('plateNumber'))
            ? (getValues('plateNumber') as unknown as string)
            : undefined,
          effectiveDate: DateService.formatDateBackend(
            getValues('effectiveDate')
          ),
          product: product,
          chassis: getValues('chassis'),
          motor: getValues('motor'),
        },
      }).then((response) => {
        if (
          response.data.Insurance?.queries
            ?.checkPlateNumberHasActiveProductPolicy.length > 0
        ) {
          const plateNumberAlreadyExistsErrorMessage = `Current ${
            response.data?.Insurance?.queries
              ?.checkPlateNumberHasActiveProductPolicy[0]
              ?.insurancePolicyMotorDetails_Product.Title
          } 
                policy exists for this vehicle with Policy Number ${
                  response.data?.Insurance?.queries
                    ?.checkPlateNumberHasActiveProductPolicy[0]
                    ?.insurance_Policy_FullPolicyNumber
                } that expires on ${DateService.formatDate(response.data?.Insurance?.queries?.checkPlateNumberHasActiveProductPolicy[0]?.insurance_Policy_PolicyExpiryDate)}`;
          setError(
            `planDetails.${lineIndex}.product` as keyof typeof initialValues,
            {
              type: 'custom',
              message: plateNumberAlreadyExistsErrorMessage,
            }
          );
        } else {
          clearErrors(
            `planDetails.${lineIndex}.product` as keyof typeof initialValues
          );
        }
      });
    }
  };

  const validateVignetteOnChange = (lineIndex: number) => {
    if (
      !isEmpty(getValues(`planDetails.${lineIndex}.vignetteCode`)) &&
      !isEmpty(getValues(`planDetails.${lineIndex}.vignette`))
    ) {
      validateVignetteLazy({
        variables: {
          entityId: params.id,
          effectiveDate: DateService.formatDateBackend(
            getValues('effectiveDate')
          ),
          vignetteCode: getValues(`planDetails.${lineIndex}.vignetteCode`),
          vignette: Number(getValues(`planDetails.${lineIndex}.vignette`)),
        },
      })
        .then((response) => {
          if (
            response.data.Insurance?.queries
              ?.checkDuplicateVignetteHasActivePolicy.length > 0
          ) {
            const vignetteAlreadyExistsErrorMessage = 'Vignette already in use';

            setError(
              `planDetails.${lineIndex}.vignette` as keyof typeof initialValues,
              {
                type: 'custom',
                message: vignetteAlreadyExistsErrorMessage,
              }
            );
          } else {
            clearErrors(
              `planDetails.${lineIndex}.vignette` as keyof typeof initialValues
            );
          }
        })
        .catch((error) => {
          if (
            error instanceof ApolloError &&
            error.message === 'Vignette Number is not assigned to you'
          ) {
            const vignetteAlreadyExistsErrorMessage = error.message;

            setError(
              `planDetails.${lineIndex}.vignette` as keyof typeof initialValues,
              {
                type: 'custom',
                message: vignetteAlreadyExistsErrorMessage,
              }
            );
          } else if (
            error instanceof ApolloError &&
            error.message === 'Vignette already in use'
          ) {
            const vignetteAlreadyExistsErrorMessage = error.message;

            setError(
              `planDetails.${lineIndex}.vignette` as keyof typeof initialValues,
              {
                type: 'custom',
                message: vignetteAlreadyExistsErrorMessage,
              }
            );
          } else {
            clearErrors(
              `planDetails.${lineIndex}.vignette` as keyof typeof initialValues
            );
          }
        });
    }
  };

  const getCopyPolicyDetails = (policyNumber: string) => {
    copyPolicyDetailsLazy({
      variables: {
        applicationId: entityId,
        enteredPolicyNumber: policyNumber,
      },
    })
      .then((result) => {
        const getPolicyByNumber =
          result?.data?.Insurance?.queries?.getPolicyByNumber;
        if (getPolicyByNumber.length === 0) {
          toast.error(
            <ToastErrorMessage>
              Policy Number does not exist in the system.
            </ToastErrorMessage>
          );
        } else {
          const policyData = getPolicyByNumber[0];

          setValue(
            'nameOnLicenseAr',
            policyData.insurancePolicyMotorDetails_NameOnLicenseAr
          );
          setValue('email', policyData.insurancePolicyHolder_Email);
          setValue(
            'mobileNumber',
            policyData.insurancePolicyHolder_MobileNumber
          );
          setValue(
            'country',
            policyData.insurancePolicyHolder_Address.Country?.Code
          );
          setValue('city', policyData.insurancePolicyHolder_Address?.City);
          setValue('street', policyData.insurancePolicyHolder_Address?.Street);
          setValue(
            'building',
            policyData.insurancePolicyHolder_Address?.Building
          );
          setValue('title', policyData.insurancePolicyHolder_Title?.Code);
          setValue('fullName', policyData.insurancePolicyHolder_FullName);
          setValue('firstName', policyData.insurancePolicyHolder_FirstName);
          setValue('middleName', policyData.insurancePolicyHolder_MiddleName);
          setValue('lastName', policyData.insurancePolicyHolder_LastName);
          setValue('phoneType', policyData.insurancePolicyHolder_PhoneType);
          setValue(
            'carNationality',
            policyData.insurancePolicyMotorDetails_CarNationality?.Code ||
              (isCashPlusAgency(applicationInfo?.agencyName)
                ? DEFAULT_CAR_NATIONALITY
                : '')
          );
          setValue(
            'plateCode',
            policyData.insurancePolicyMotorDetails_PlateCode?.Code
          );
          setValue(
            'plateNumber',
            policyData.insurancePolicyMotorDetails_PlateNumber
          );
          setValue(
            'usageType',
            policyData.insurancePolicyMotorDetails_UsageType?.Code
          );
          setUsageTypes(adjustUsageTypesListAndReview());

          const bodyTypesList = filterBodyTypeLOVs(
            policyData.insurancePolicyMotorDetails_UsageType?.Code,
            lovs.bodyTypes,
            getValues('carNationality')
          );
          setBodyTypes(bodyTypesList);

          const bodyType =
            policyData.insurancePolicyMotorDetails_BodyType?.Code;
          setValue('bodyType', bodyType);

          if (!isEmpty(bodyType)) {
            const policyWeight =
              policyData.insurancePolicyMotorDetails_Weight;
            const policyModel =
              policyData.insurancePolicyMotorDetails_Model?.Code;
            const policyBrand =
              policyData.insurancePolicyMotorDetails_Brand?.Code;
            setTowingService(
              filterTowingServiceLOVs(
                bodyType,
                lovs.towingServices,
                policyWeight
              )
            );
            setBreakdownServices(
              filterBreakdownServicesLOVs(
                bodyType,
                lovs.breakdownServices,
                policyWeight,
                policyModel,
                policyBrand
              )
            );
            checkBreakdownServicesValue(
              filterBreakdownServicesLOVs(
                bodyType,
                lovs.breakdownServices,
                policyWeight,
                policyModel,
                policyBrand
              )
            );
          }

          setValue(
            'engineType',
            policyData.insurancePolicyMotorDetails_EngineType?.Code
          );
          setValue('brand', policyData.insurancePolicyMotorDetails_Brand?.Code);

          getModelsByBrandsLazy({
            variables: {
              modelId: policyData.insurancePolicyMotorDetails_Brand?.Code,
            },
          });

          setValue('model', policyData.insurancePolicyMotorDetails_Model?.Code);
          setValue(
            'yearOfMake',
            policyData.insurancePolicyMotorDetails_YearOfMake
          );
          setValue('motor', policyData.insurancePolicyMotorDetails_Motor);
          setValue('weight', policyData.insurancePolicyMotorDetails_Weight);
          setValue(
            'engineSize',
            policyData.insurancePolicyMotorDetails_EngineSize
          );
          setValue('seats', policyData.insurancePolicyMotorDetails_Seats);
          setValue('chassis', policyData.insurancePolicyMotorDetails_Chassis);
          setValue(
            'horsePower',
            policyData.insurancePolicyMotorDetails_HorsePower
          );
        }
      })
      .catch((error) => {
        toast.error(<ToastErrorMessage>{error}</ToastErrorMessage>);
      });
  };

  const adjustUsageTypesListAndReview = (carNationality?: string) => {
    const usageTypes = filterUsageTypeLOVs(
      getValues('plateCode'),
      lovs.usageTypes,
      carNationality ?? getValues('carNationality')
    );

    if (!usageTypes[getValues('usageType')]) {
      setValue('usageType', '');
    }

    return usageTypes;
  };

  const adjustBodyTypesListAndReview = (
    usageType?: string,
    carNationality?: string
  ) => {
    const bodyTypesList = filterBodyTypeLOVs(
      usageType ?? getValues('usageType'),
      lovs.bodyTypes,
      carNationality ?? getValues('carNationality')
    );

    if (!bodyTypesList[getValues('bodyType')]) {
      setValue('bodyType', '');
    }

    return bodyTypesList;
  };

  const renderMainChildren = () => {
    const applicationStatus =
      entityResult?.data?.Insurance?.entities?.application?.views?.Insurance_all?.properties?.Status.toLowerCase();

    const quotes: quote[] = [];
    entityResult?.data?.Insurance?.entities?.application?.views?.Insurance_all?.properties?.MotorDetailsID?.queries?.insurance?.plans?.map(
      (plan: any, i: number) => {
        const quoteEntity = graphqlEntityToQuoteDetails(
          entityResult?.data,
          i,
          CURRENCY_SYMBOL,
          lob
        );
        logMotorBreakdownDebug('quote card row visibility', {
          quoteIndex: i,
          breakdownServicesTitle: quoteEntity.breakdownServicesTitle,
          willShowOnCard: quoteEntity.breakdownServicesTitle != null,
        });
        quotes.push(quoteEntity);
      }
    );

    return (
      <>
        <div style={{ marginTop: '20px' }}>
          <EnhancedStepper
            showStepperButton={isClosed}
            buttonTitle={'New'}
            buttonState={newButtonState}
            activeStep={getApplicationStatus(
              entityResult?.data?.Insurance?.entities?.application?.views
                ?.Insurance_all?.properties?.Status
            )}
            steps={steps}
            reverseButton={true}
            className={classes.stepper}
            buttonOnClick={() => {
              setNewButtonState('loading');
              checkSubmission();
            }}
          />
          {applicationStatus != 'new' && (
            <>
              <QuotesCards
                entityId={entityId}
                applicationStatus={applicationStatus}
                quotes={quotes}
                sendQuoteAction={sendQuoteAction}
                acceptAction={acceptAction}
                issuePolicyAction={issuePolicyAction}
                downloadQuoteAction={downloadQuoteAction}
                previewPoliciesAction={previewPoliciesAction}
                customerEmail={getValues('email') || null}
                disabled={formDisabled || !!isDirty}
                userCanSend={
                  entityResult?.data?.Insurance?.entities?.application?.actions
                    ?.able_To_SendQuotes
                }
                disabledSendButton={disabledSendBtn}
                disabledAcceptButton={disabledAcceptBtn}
                disabledIssuePolicyButton={disabledIssuePolicyBtn}
                userCanAccept={
                  entityResult?.data?.Insurance?.entities?.application?.actions
                    ?.able_To_AcceptQuotes
                }
                userCanIssue={
                  entityResult?.data?.Insurance?.entities?.application?.actions
                    ?.able_To_IssuePolicy
                }
                userCanDownloadQuote={
                  entityResult?.data?.Insurance?.entities?.application?.actions
                    ?.able_To_PreviewQuotes
                }
                userCanPreviewPolicies={
                  entityResult?.data?.Insurance?.entities?.application?.actions
                    ?.able_To_PreviewPolicies
                }
                lineOfBusiness={lob}
                quoteInputs={{
                  applicationContactInputs: {
                    building: isEmpty(getValues('building'))
                      ? null
                      : getValues('building'),
                    city: getValues('city'),
                    country: getValues('country'),
                    email: isEmpty(getValues('email'))
                      ? null
                      : getValues('email'),
                    fullName:
                      getValues('firstName') +
                      ' ' +
                      getValues('middleName') +
                      ' ' +
                      getValues('lastName'),
                    mobileNumber: getValues('mobileNumber'),
                    street: getValues('street'),
                    title: getValues('title'),
                  },
                  hasMBI,
                }}
                recomputeHasMBI={() => computeHasMBI()}
              />
              <Separator className={classes.thickSeperator} />
            </>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <WidgetPaper>
              <WidgetSection
                title="Application Details"
                narrowSpacing={true}
                style={{ margin: '-20px 0' }}
                classes={{ container: classes.section }}
                actions={
                  <>
                    {!isClosed && (
                      <ShowForUser allowedRoles={['Insurance-IssuingAgent']}>
                        <EnhancedButton
                          type="button"
                          backgroundColor={MAIN_THEME.color.black}
                          color={MAIN_THEME.color.white}
                          style={{ display: 'inline', margin: '0 0 -15px 5px' }}
                          onClick={() => {
                            setCopyPolicyDetailsDrawerOpen(true);
                          }}
                        >
                          Copy Policy Details
                        </EnhancedButton>
                      </ShowForUser>
                    )}
                    {!isClosed && isEnabled && (
                      <EnhancedButton
                        type={buttonType}
                        state={submitButtonState}
                        backgroundColor={MAIN_THEME.color.black}
                        color={MAIN_THEME.color.white}
                        style={{ display: 'inline', margin: '0 0 -15px 5px' }}
                        onClick={
                          buttonType === 'button'
                            ? () => {
                                logMotorDebug(
                                  'calculate',
                                  'Calculate clicked – validation blocking submit',
                                  { errorKeys: Object.keys(errors), errors }
                                );
                                void handleSubmit(onSubmit)();
                              }
                            : undefined
                        }
                      >
                        {buttonLabel}
                      </EnhancedButton>
                    )}
                  </>
                }
                middle={
                  isDirty &&
                  displayDirtyMessage.current > 0 &&
                  !isNew &&
                  !isClosed ? (
                    <Chip
                      label={
                        'Form fields changed, please re-calculate to update the application accordingly'
                      }
                      className={classes.dirtyChip}
                    />
                  ) : (
                    <></>
                  )
                }
                useSeparator={false}
              >
                <Controller
                  name="effectiveDate"
                  control={control}
                  rules={{
                    required: 'Required',
                    validate: (value) =>
                      DateService.isValidDate(value) || 'Invalid Date',
                  }}
                  render={({ field: { onBlur, onChange, value } }) => (
                    <DatePickerFormField
                      name={'effectiveDate'}
                      onBlur={onBlur}
                      onDateChange={(e: any) => {
                        onChange(e);
                        trigger('effectiveDate' as keyof typeof initialValues);

                        const selectedDate = dayjs(e);
                        const effectiveDate = selectedDate
                          .toDate()
                          .toDateString();
                        const expiryDate = selectedDate
                          .add(1, 'y')
                          .toDate()
                          .toDateString();

                        setValue('effectiveDate', effectiveDate);
                        setValue('expiryDate', expiryDate);

                        planDetailsFieldArray.fields.forEach(
                          (data, rowIndex) => {
                            validatePlateNumberOnProductChange(
                              data.product as string,
                              rowIndex
                            );
                          }
                        );
                      }}
                      title="Effective Date*"
                      material
                      value={
                        !isEmpty(value) ? value : initialValues.effectiveDate
                      }
                      disabled={formDisabled}
                      className={classes.field}
                      error={errors?.effectiveDate?.message}
                      minDate={
                        userAgentType === 'INTERNAL'
                          ? DateService.subtractMonths(new Date(), 3)
                          : new Date()
                      }
                      maxDate={DateService.getDateBasedOnMonth()}
                    />
                  )}
                />
                <Controller
                  name="expiryDate"
                  control={control}
                  render={({ field: { onBlur, onChange, value } }) => (
                    <DatePickerFormField
                      name={'expiryDate'}
                      onBlur={onBlur}
                      onDateChange={() => undefined}
                      title="Expiry Date*"
                      material
                      disabled
                      value={value}
                      className={classes.field}
                    />
                  )}
                />
                <Controller
                  name={'isRenewal'}
                  control={control}
                  rules={{ required: 'required' }}
                  key={'isRenewal'}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedChipInput
                      name={'isRenewal'}
                      title="Renewal*"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = e as string;
                        onChange(newValue);
                        trigger(`isRenewal` as keyof typeof initialValues);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        trigger('isRenewal' as keyof typeof initialValues);
                      }}
                      selectOptions={lovs?.renewalStatus}
                      material
                      error={errors?.isRenewal?.message}
                      multiple={false}
                      disabled={formDisabled}
                      className={classes.field}
                    />
                  )}
                />
                {getValues('isRenewal') === 'YES' && (
                  <Controller
                    name="renewalNumber"
                    control={control}
                    rules={{
                      required: {
                        value: getValues('isRenewal') === 'YES',
                        message: 'Required',
                      },
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <EnhancedInput
                        type="text"
                        name="renewalNumber"
                        title="Renewal Number*"
                        onChange={(e) => {
                          trigger(
                            'renewalNumber' as keyof typeof initialValues
                          );
                          onChange(e);
                        }}
                        onBlur={async (e) => {
                          onBlur();
                          trigger(
                            'renewalNumber' as keyof typeof initialValues
                          );

                          if (
                            getValues('isRenewal') === 'YES' &&
                            !isEmpty(getValues('renewalNumber'))
                          ) {
                            const { data } = await getPolicyByNumberLazy({
                              variables: {
                                id: entityId,
                                policyNumber: getValues('renewalNumber'),
                              },
                            });

                            const result =
                              data?.Insurance?.queries?.getPolicyByNumber;

                            const policy = result[0];

                            originalRenewalProduct.current =
                              policy.insurancePolicyMotorDetails_Product?.Id ||
                              null;

                            const expiry =
                              policy.insurance_Policy_PolicyExpiryDate;
                            const today = DateService.formatDateBackend(
                              new Date()
                            );
                            const todayPlus1Month =
                              DateService.formatDateBackend(
                                DateService.addMonths(today, 1)
                              );

                            setValue(
                              'effectiveDate',
                              DateService.isDateBefore(expiry, today)
                                ? today
                                : DateService.isDateBefore(
                                      todayPlus1Month,
                                      expiry
                                    )
                                  ? todayPlus1Month
                                  : expiry
                            );

                            setValue(
                              'nameOnLicenseEn',
                              policy.insurancePolicyMotorDetails_NameOnLicenseEn
                            );
                            setValue(
                              'nameOnLicenseEn',
                              policy.insurancePolicyMotorDetails_NameOnLicenseEn
                            );
                            setValue(
                              'nameOnLicenseAr',
                              policy.insurancePolicyMotorDetails_NameOnLicenseAr
                            );
                            setValue(
                              'email',
                              policy.insurancePolicyHolder_Email
                            );
                            setValue(
                              'mobileNumber',
                              policy.insurancePolicyHolder_MobileNumber
                            );
                            setValue(
                              'country',
                              policy.insurancePolicyHolder_Address?.Country
                                ?.Code
                            );
                            setValue(
                              'city',
                              policy.insurancePolicyHolder_Address?.City
                            );
                            setValue(
                              'street',
                              policy.insurancePolicyHolder_Address?.Street
                            );
                            setValue(
                              'building',
                              policy.insurancePolicyHolder_Address?.Building
                            );
                            setValue(
                              'title',
                              policy.insurancePolicyHolder_Title?.Code
                            );
                            setValue(
                              'fullName',
                              policy.insurancePolicyHolder_FullName
                            );
                            setValue(
                              'firstName',
                              policy.insurancePolicyHolder_FirstName
                            );
                            setValue(
                              'middleName',
                              policy.insurancePolicyHolder_MiddleName
                            );
                            setValue(
                              'lastName',
                              policy.insurancePolicyHolder_LastName
                            );
                            setValue(
                              'phoneType',
                              policy.insurancePolicyHolder_PhoneType
                            );

                            setValue(
                              'carNationality',
                              policy.insurancePolicyMotorDetails_CarNationality
                                ?.Code ||
                                (isCashPlusAgency(applicationInfo?.agencyName)
                                  ? DEFAULT_CAR_NATIONALITY
                                  : '')
                            );
                            setValue(
                              'plateCode',
                              policy.insurancePolicyMotorDetails_PlateCode?.Code
                            );
                            setValue(
                              'plateNumber',
                              policy.insurancePolicyMotorDetails_PlateNumber
                            );
                            setValue(
                              'usageType',
                              policy.insurancePolicyMotorDetails_UsageType?.Code
                            );
                            setUsageTypes(adjustUsageTypesListAndReview());

                            const bodyTypesList = filterBodyTypeLOVs(
                              policy.insurancePolicyMotorDetails_UsageType
                                ?.Code,
                              lovs.bodyTypes,
                              getValues('carNationality')
                            );
                            setBodyTypes(bodyTypesList);

                            const bodyType =
                              policy.insurancePolicyMotorDetails_BodyType?.Code;
                            setValue('bodyType', bodyType);

                            if (!isEmpty(bodyType)) {
                              const policyWeight =
                                policy.insurancePolicyMotorDetails_Weight;
                              const policyModel =
                                policy.insurancePolicyMotorDetails_Model?.Code;
                              const policyBrand =
                                policy.insurancePolicyMotorDetails_Brand?.Id;
                              setTowingService(
                                filterTowingServiceLOVs(
                                  bodyType,
                                  lovs.towingServices,
                                  policyWeight
                                )
                              );
                              setBreakdownServices(
                                filterBreakdownServicesLOVs(
                                  bodyType,
                                  lovs.breakdownServices,
                                  policyWeight,
                                  policyModel,
                                  policyBrand
                                )
                              );
                            }

                            setValue(
                              'engineType',
                              policy.insurancePolicyMotorDetails_EngineType
                                ?.Code
                            );
                            const brandCode =
                              policy.insurancePolicyMotorDetails_Brand?.Id;
                            setValue('brand', brandCode);

                            if (brandCode) {
                              getModelsByBrandsLazy({
                                variables: {
                                  modelId: brandCode,
                                },
                              });
                            }

                            setValue(
                              'model',
                              policy.insurancePolicyMotorDetails_Model?.Code
                            );
                            setValue(
                              'yearOfMake',
                              policy.insurancePolicyMotorDetails_YearOfMake
                            );
                            setValue(
                              'motor',
                              policy.insurancePolicyMotorDetails_Motor
                            );
                            setValue(
                              'weight',
                              policy.insurancePolicyMotorDetails_Weight
                            );
                            setValue(
                              'engineSize',
                              policy.insurancePolicyMotorDetails_EngineSize
                            );
                            setValue(
                              'seats',
                              policy.insurancePolicyMotorDetails_Seats
                            );
                            setValue(
                              'chassis',
                              policy.insurancePolicyMotorDetails_Chassis
                            );
                            setValue(
                              'horsePower',
                              policy.insurancePolicyMotorDetails_HorsePower
                            );

                            setValue(
                              'planDetails.0.name',
                              policy.insurancePolicyMotorDetails_Product?.Title
                            );
                            // setValue(
                            //   'planDetails.0.planId',
                            //   policy.insurancePolicyMotorDetails_Product
                            //     ?.Title
                            // );
                            setValue(
                              'planDetails.0.product',
                              policy.insurancePolicyMotorDetails_Product?.Code
                            );
                            trigger('planDetails.0.product');
                            setValue(
                              'planDetails.0.vignetteCode',
                              policy.insurancePolicyMotorDetails_VignetteCode
                            );
                            setValue(
                              'planDetails.0.vignette',
                              policy.insurancePolicyMotorDetails_Vignette
                            );
                            setValue(
                              'planDetails.0.towingService',
                              policy.insurancePolicyMotorDetails_TowingService
                                ?.Code
                            );
                            setValue(
                              'planDetails.0.breakdownServices',
                              policy
                                .insurancePolicyMotorDetails_BreakdownService
                                ?.Code
                            );

                            await trigger('planDetails.0.product');
                          }
                        }}
                        value={!isEmpty(value) ? value : ''}
                        error={errors?.renewalNumber?.message}
                        className={classes.field}
                        material
                        disabled={formDisabled}
                      />
                    )}
                  />
                )}
              </WidgetSection>
              {lovs != null && (
                <>
                  <WidgetSection
                    title="Vehicle Details"
                    narrowSpacing={true}
                    classes={{ container: classes.section }}
                    useSeparator={false}
                    style={{ margin: '-4px 0' }}
                  >
                    {isCashPlusAgency(applicationInfo?.agencyName) && (
                      <Controller
                        name={'carNationality'}
                        control={control}
                        key={'carNationality'}
                        rules={{ required: 'required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedChipInput
                            name={'carNationality'}
                            title="Car Nationality*"
                            placeholder={'Car Nationality'}
                            value={value || DEFAULT_CAR_NATIONALITY}
                            onChange={(e) => {
                              const newValue = e as string;
                              onChange(newValue);
                              trigger(
                                `carNationality` as keyof typeof initialValues
                              );
                              setUsageTypes(
                                adjustUsageTypesListAndReview(newValue)
                              );
                              setBodyTypes(
                                adjustBodyTypesListAndReview(
                                  undefined,
                                  newValue
                                )
                              );
                            }}
                            onBlur={() => {
                              onBlur();
                              trigger(
                                'carNationality' as keyof typeof initialValues
                              );
                            }}
                            selectOptions={lovs?.carNationalities}
                            material
                            error={errors?.carNationality?.message}
                            multiple={false}
                            disabled={formDisabled}
                            className={classes.field}
                          />
                        )}
                      />
                    )}
                    <Controller
                      name={'plateCode'}
                      control={control}
                      key={'plateCode'}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedChipInput
                          name={'plateCode'}
                          title="Plate Code"
                          placeholder={'Plate Code'}
                          value={value || ''}
                          onChange={(e) => {
                            const newValue = e as string;
                            onChange(newValue);
                            trigger(`plateCode` as keyof typeof initialValues);
                            planDetailsFieldArray.fields.forEach(
                              (data, rowIndex) => {
                                validatePlateNumberOnProductChange(
                                  data.product as string,
                                  rowIndex
                                );
                              }
                            );

                            setUsageTypes(adjustUsageTypesListAndReview());
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger('plateCode' as keyof typeof initialValues);
                          }}
                          selectOptions={lovs?.plateCodes}
                          material
                          error={errors?.plateCode?.message}
                          multiple={false}
                          disabled={formDisabled}
                          className={classes.field}
                        />
                      )}
                    />
                    <Controller
                      name={'plateNumber'}
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedInput
                          type="text"
                          name={'plateNumber'}
                          title="Plate Number"
                          placeholder={'Plate Number'}
                          value={!isEmpty(value) ? value : ''}
                          onChange={(e) => {
                            onChange(e);
                            trigger(
                              `plateNumber` as keyof typeof initialValues
                            );
                            planDetailsFieldArray.fields.forEach(
                              (data, rowIndex) => {
                                validatePlateNumberOnProductChange(
                                  data.product as string,
                                  rowIndex
                                );
                              }
                            );
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger(
                              'plateNumber' as keyof typeof initialValues
                            );
                          }}
                          material
                          error={errors?.plateNumber?.message}
                          disabled={formDisabled}
                          className={classes.field}
                        />
                      )}
                    />
                    <Controller
                      name={'usageType'}
                      control={control}
                      rules={{ required: 'required' }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedChipInput
                          name={'usageType'}
                          title="Usage*"
                          placeholder={'Usage'}
                          onChange={(e) => {
                            const newValue = e as string;
                            onChange(newValue);
                            trigger(`usageType` as keyof typeof initialValues);
                            setBodyTypes(
                              adjustBodyTypesListAndReview(newValue)
                            );
                          }}
                          multiple={false}
                          onBlur={(e) => {
                            onBlur();
                            trigger('usageType' as keyof typeof initialValues);
                          }}
                          material
                          value={value || ''}
                          selectOptions={usageTypes}
                          error={errors?.usageType?.message}
                          disabled={formDisabled}
                          className={classes.field}
                        />
                      )}
                    />
                    <Controller
                      name={'bodyType'}
                      control={control}
                      rules={{ required: 'required' }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedChipInput
                          name={'bodyType'}
                          title="Body*"
                          placeholder={'Body'}
                          onChange={(e) => {
                            const newValue = e as string;
                            onChange(newValue);
                            trigger(`bodyType` as keyof typeof initialValues);
                            !isEmpty(getValues('seats'))
                              ? trigger('seats' as keyof typeof initialValues)
                              : '';
                            reviewTowingService();
                            reviewBreakdownServices();
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger('bodyType' as keyof typeof initialValues);
                          }}
                          selectOptions={bodyTypes}
                          material
                          value={value || ''}
                          error={errors?.bodyType?.message}
                          multiple={false}
                          disabled={
                            formDisabled ||
                            !bodyTypes ||
                            Object.keys(bodyTypes).length == 0
                          }
                          className={classes.field}
                        />
                      )}
                    />
                    <Controller
                      name={'engineType'}
                      control={control}
                      rules={{ required: 'required' }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedChipInput
                          name={'engineType'}
                          title="Engine Type*"
                          placeholder={'Engine Type'}
                          onChange={(e) => {
                            onChange(e);
                            trigger(`motor` as keyof typeof initialValues);
                            planDetailsFieldArray.fields.forEach(
                              (data, rowIndex) => {
                                validatePlateNumberOnProductChange(
                                  data.product as string,
                                  rowIndex
                                );
                              }
                            );
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger('engineType' as keyof typeof initialValues);
                          }}
                          material
                          value={value || ''}
                          selectOptions={lovs.engineTypes}
                          error={errors?.engineType?.message}
                          multiple={false}
                          disabled={formDisabled}
                          className={classes.field}
                        />
                      )}
                    />
                    <Controller
                      name={'brand'}
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedChipInput
                          name={'brand'}
                          title="Make*"
                          placeholder={'Make'}
                          onChange={(e) => {
                            const brandValue = e as string;
                            onChange(brandValue);
                            trigger(`brand` as keyof typeof initialValues);
                            getModelsByBrandsLazy({
                              variables: { modelId: brandValue },
                            });
                            reviewBreakdownServices();
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger('brand' as keyof typeof initialValues);
                          }}
                          material
                          value={value || ''}
                          error={errors?.brand?.message}
                          selectOptions={lovs.brands}
                          multiple={false}
                          disabled={formDisabled}
                          className={classes.field}
                        />
                      )}
                    />
                    <Controller
                      name={'model'}
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedChipInput
                          name={'model'}
                          title="Model*"
                          placeholder={'Model'}
                          onChange={(e) => {
                            const newValue = e as string;
                            onChange(newValue);
                            trigger(`model` as keyof typeof initialValues);
                            reviewBreakdownServices();
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger('model' as keyof typeof initialValues);
                          }}
                          material
                          selectOptions={lovs.models}
                          value={value || ''}
                          error={errors?.model?.message}
                          multiple={false}
                          disabled={formDisabled || isEmpty(getValues('brand'))}
                          loader={loadingModelsState}
                          className={classes.field}
                        />
                      )}
                    />
                    <Controller
                      name={'yearOfMake'}
                      control={control}
                      rules={{
                        required: 'required',
                        min: {
                          value: 1900,
                          message: 'Invalid Year Of Make',
                        },
                        max: {
                          value: 2100,
                          message: 'Invalid Year Of Make',
                        },
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedNumberInput
                          name={'yearOfMake'}
                          title="Year of Make*"
                          placeholder={'Year of Make'}
                          onChange={(e) => {
                            onChange(e);
                            trigger(`yearOfMake` as keyof typeof initialValues);
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger('yearOfMake' as keyof typeof initialValues);
                          }}
                          material
                          value={
                            !isEmpty(value)
                              ? value
                              : initialValues.yearOfMake || ''
                          }
                          error={errors?.yearOfMake?.message}
                          disabled={formDisabled}
                          className={classes.field}
                          minValue={1}
                          maxValue={2100}
                          disableNegativeValues
                        />
                      )}
                    />
                    <ShowForUser
                      allowedRoles={[
                        'Insurance-IssuingAgent',
                        'Insurance-Underwriter',
                        'Insurance-Admin',
                      ]}
                    >
                      <Controller
                        name={'motor'}
                        control={control}
                        rules={{ required: 'required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedInput
                            name={'motor'}
                            type="text"
                            title="Engine*"
                            placeholder={'Engine'}
                            onChange={(e) => {
                              onChange(e);
                              trigger(`motor` as keyof typeof initialValues);
                            }}
                            onBlur={(e) => {
                              onBlur();
                              trigger(`motor` as keyof typeof initialValues);
                              planDetailsFieldArray.fields.forEach(
                                (data, rowIndex) => {
                                  validatePlateNumberOnProductChange(
                                    data.product as string,
                                    rowIndex
                                  );
                                }
                              );
                            }}
                            material
                            value={
                              !isEmpty(value) ? value : initialValues.motor
                            }
                            error={errors?.motor?.message}
                            disabled={formDisabled}
                            className={classes.field}
                          />
                        )}
                      />
                    </ShowForUser>
                    {(getValues('bodyType') === 'VAN' ||
                      getValues('bodyType') === 'PICKUP' ||
                      getValues('bodyType') === 'TRUCK' ||
                      getValues('bodyType') === 'F_TANKER') && (
                      <Controller
                        name={'weight'}
                        control={control}
                        rules={{
                          required: 'required',
                          validate: {
                            positiveWeight: (value) =>
                              value > 0 || 'Invalid Weight',
                            maxWeight: (value) =>
                              value <= 99 || 'Invalid Weight',
                          },
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedNumberInput
                            name={'weight'}
                            title="Weight (in Tons)*"
                            placeholder={'Weight'}
                            onChange={(e) => {
                              onChange(e);
                              trigger(`weight` as keyof typeof initialValues);
                              reviewTowingService();
                              reviewBreakdownServices();
                            }}
                            onBlur={(e) => {
                              onBlur();
                              trigger('weight' as keyof typeof initialValues);
                            }}
                            material
                            value={
                              !isEmpty(value)
                                ? value
                                : initialValues.weight || ''
                            }
                            error={errors?.weight?.message}
                            disabled={formDisabled}
                            className={classes.field}
                            disableNegativeValues
                          />
                        )}
                      />
                    )}
                    {getValues('bodyType') == 'MOTORCYCLE' && (
                      <Controller
                        name={'engineSize'}
                        control={control}
                        rules={{
                          required: 'required',
                          min: {
                            value: 1,
                            message: 'Invalid Engine Size',
                          },
                          max: {
                            value: 99999,
                            message: 'Invalid Engine Size',
                          },
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedNumberInput
                            name={'engineSize'}
                            title="Engine Size (in cc)*"
                            placeholder={'Engine Size'}
                            onChange={(e) => {
                              onChange(e);
                              trigger(
                                `engineSize` as keyof typeof initialValues
                              );
                            }}
                            onBlur={(e) => {
                              onBlur();
                              trigger(
                                'engineSize' as keyof typeof initialValues
                              );
                            }}
                            material
                            value={
                              !isEmpty(value)
                                ? value
                                : initialValues.engineSize || ''
                            }
                            error={errors?.engineSize?.message}
                            disabled={formDisabled}
                            className={classes.field}
                            disableNegativeValues
                          />
                        )}
                      />
                    )}
                    <Controller
                      name={'seats'}
                      control={control}
                      rules={{
                        required: 'required',
                        min: {
                          value: 1,
                          message: 'Invalid Seats',
                        },
                        max: {
                          value: 99,
                          message: 'Invalid Seats',
                        },
                        // // validate: (value, { bodyType }) => {
                        // //   if (
                        // //     bodyType.toLowerCase() === "sedan" ||
                        // //     bodyType.toLowerCase() === "station"
                        // //   ) {
                        // //     if (value > 5) {
                        // //       return "Vehicle cannot have more than 5 seats";
                        // //     }
                        // //   }
                        // //   return true;
                        // // },
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <EnhancedNumberInput
                          name={'seats'}
                          title="Number of Seats*"
                          placeholder={'Number of Seats'}
                          onChange={(e) => {
                            onChange(e);
                            trigger(`seats` as keyof typeof initialValues);
                            reviewTowingService();
                          }}
                          onBlur={(e) => {
                            onBlur();
                            trigger('seats' as keyof typeof initialValues);
                          }}
                          material
                          value={
                            !isEmpty(value) ? value : initialValues.seats || ''
                          }
                          error={errors?.seats?.message}
                          disabled={formDisabled}
                          className={classes.field}
                          disableNegativeValues
                        />
                      )}
                    />

                    <ShowForUser
                      allowedRoles={[
                        'Insurance-IssuingAgent',
                        'Insurance-Underwriter',
                        'Insurance-Admin',
                      ]}
                    >
                      <Controller
                        name={'chassis'}
                        control={control}
                        rules={{
                          required: 'required',
                          minLength: {
                            value: 5,
                            message:
                              'value must have between 5 and 20 characters',
                          },
                          maxLength: {
                            value: 20,
                            message:
                              'value must have between 5 and 20 characters',
                          },
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedInput
                            name={'chassis'}
                            type="text"
                            title="Chassis*"
                            placeholder={'Chassis'}
                            onChange={(e) => {
                              onChange(e);
                              trigger(`chassis` as keyof typeof initialValues);
                            }}
                            onBlur={(e) => {
                              onBlur();
                              trigger(`chassis` as keyof typeof initialValues);
                              planDetailsFieldArray.fields.forEach(
                                (data, rowIndex) => {
                                  validatePlateNumberOnProductChange(
                                    data.product as string,
                                    rowIndex
                                  );
                                }
                              );
                            }}
                            material
                            value={
                              !isEmpty(value) ? value : initialValues.chassis
                            }
                            error={errors?.chassis?.message}
                            disabled={formDisabled}
                            className={classes.field}
                          />
                        )}
                      />
                      <Controller
                        name={'horsePower'}
                        control={control}
                        rules={{ required: 'required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedNumberInput
                            name={'horsePower'}
                            title="Horse Power*"
                            placeholder={'Horse Power'}
                            onChange={(e) => {
                              onChange(e);
                              trigger(
                                `horsePower` as keyof typeof initialValues
                              );
                            }}
                            onBlur={(e) => {
                              onBlur();
                              trigger(
                                'horsePower' as keyof typeof initialValues
                              );
                            }}
                            material
                            value={
                              !isEmpty(value)
                                ? value
                                : initialValues.horsePower || ''
                            }
                            error={errors?.horsePower?.message}
                            disabled={formDisabled}
                            className={classes.field}
                            disableNegativeValues
                          />
                        )}
                      />
                    </ShowForUser>
                  </WidgetSection>
                </>
              )}
              <WidgetSection
                title="Customer Details"
                narrowSpacing={true}
                classes={{
                  container: classes.section,
                }}
                useSeparator={false}
                style={{ margin: '-4px 0' }}
              >
                {lovs != null && (
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <EnhancedChipInput
                        name={`title`}
                        title="Title*"
                        placeholder={'Title'}
                        value={!isEmpty(value) ? value : ''}
                        onChange={(e) => {
                          onChange(e as string);
                          trigger(`title` as keyof typeof initialValues);
                        }}
                        onBlur={(e) => {
                          onBlur();
                          trigger('title' as keyof typeof initialValues);
                        }}
                        selectOptions={lovs?.titles}
                        error={
                          errors.title != undefined && !isEmpty(errors?.title)
                            ? errors?.title?.message
                            : null
                        }
                        multiple={false}
                        material
                        disabled={formDisabled}
                        className={classes.field}
                      />
                    )}
                  />
                )}

                <Controller
                  name="firstName"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInput
                      type="text"
                      name="firstName"
                      title="First Name*"
                      placeholder={'First Name'}
                      onChange={(e) => {
                        trigger('firstName' as keyof typeof initialValues);
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        if (!isEmpty(getValues('firstName'))) {
                          autoSetNameOnLicenseEn(
                            e.target.value,
                            getValues('lastName')
                          );
                        }
                        trigger('firstName' as keyof typeof initialValues);
                        onBlur();
                      }}
                      value={!isEmpty(value) ? value : ''}
                      error={errors?.firstName?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                    />
                  )}
                />
                <Controller
                  name="middleName"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInput
                      type="text"
                      name="middleName"
                      title="Middle Name"
                      placeholder={'Middle Name'}
                      onChange={(e) => {
                        trigger('middleName' as keyof typeof initialValues);
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        trigger('middleName' as keyof typeof initialValues);
                        onBlur();
                      }}
                      value={!isEmpty(value) ? value : ''}
                      error={errors?.middleName?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInput
                      type="text"
                      name="lastName"
                      title="Last Name*"
                      placeholder={'Last Name'}
                      onChange={(e) => {
                        trigger('lastName' as keyof typeof initialValues);
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        if (!isEmpty(getValues('lastName'))) {
                          autoSetNameOnLicenseEn(
                            getValues('firstName'),
                            e.target.value
                          );
                        }
                        trigger('lastName' as keyof typeof initialValues);
                        onBlur();
                      }}
                      value={!isEmpty(value) ? value : ''}
                      error={errors?.lastName?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                    />
                  )}
                />

                {true && (
                  <>
                    <ShowForUser
                      allowedRoles={[
                        'Insurance-IssuingAgent',
                        'Insurance-Underwriter',
                        'Insurance-Admin',
                      ]}
                    >
                      <Controller
                        name="nameOnLicenseEn"
                        control={control}
                        rules={{ required: 'Required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedInput
                            type="text"
                            name="nameOnLicenseEn"
                            title="Name on License (EN)*"
                            placeholder={'Name on License (EN)'}
                            onChange={(e) => {
                              trigger(
                                'nameOnLicenseEn' as keyof typeof initialValues
                              );
                              onChange(e);
                              autoSetNameOnLicenseAr(e.target.value);
                            }}
                            onBlur={(e) => {
                              trigger(
                                'nameOnLicenseEn' as keyof typeof initialValues
                              );
                              onBlur();
                            }}
                            value={!isEmpty(value) ? value : ''}
                            error={errors?.nameOnLicenseEn?.message}
                            className={classes.field}
                            material
                            disabled={formDisabled}
                            loader={loadingNameOnLicenseEnState}
                          />
                        )}
                      />
                      <Controller
                        name="nameOnLicenseAr"
                        control={control}
                        rules={{ required: 'Required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <EnhancedInput
                            type="text"
                            name="nameOnLicenseAr"
                            title="Name on License (AR)*"
                            placeholder={'إسم وشهرة المالك'}
                            onChange={(e) => {
                              onChange(e);

                              trigger(
                                'nameOnLicenseAr' as keyof typeof initialValues
                              );
                            }}
                            onBlur={(e) => {
                              onBlur();
                              trigger(
                                'nameOnLicenseAr' as keyof typeof initialValues
                              );
                            }}
                            value={!isEmpty(value) ? value : ''}
                            error={errors?.nameOnLicenseAr?.message}
                            className={classes.arField}
                            material
                            disabled={formDisabled}
                            loader={loadingNameOnLicenseArState}
                            rtlContent
                          />
                        )}
                      />
                    </ShowForUser>
                  </>
                )}
                <Controller
                  name="mobileNumber"
                  control={control}
                  rules={{
                    validate: {
                      requried: (value) =>
                        checkPhoneNumber(value) || 'required',
                    },
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInternationalPhoneInput
                      name={'mobileNumber'}
                      title={'Phone Number*'}
                      placeholder={'Phone Number'}
                      onChange={(e) => {
                        onChange(e);
                        trigger('mobileNumber' as keyof typeof initialValues);

                        if (isEmpty(e)) {
                          setValue('mobileNumber', undefined);
                        }
                      }}
                      onBlur={(e) => {
                        onBlur();

                        if (isEmpty(e)) {
                          setValue('mobileNumber', undefined);
                        }

                        trigger('mobileNumber' as keyof typeof initialValues);
                      }}
                      value={!isEmpty(value) ? value : ''}
                      error={errors?.mobileNumber?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                      disableDropDown={false}
                      editCountryCode={false}
                      defaultCountry={'lb'}
                    />
                  )}
                />

                {lovs != null && (
                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <EnhancedChipInput
                        name={`country`}
                        title="Country*"
                        placeholder={'Country'}
                        value={!isEmpty(value) ? value : ''}
                        onChange={(e) => {
                          onChange(e as string);
                          trigger(`country` as keyof typeof initialValues);
                        }}
                        onBlur={(e) => {
                          onBlur();
                          trigger('country' as keyof typeof initialValues);
                        }}
                        selectOptions={lovs?.countries}
                        error={
                          errors.country != undefined &&
                          !isEmpty(errors?.country)
                            ? errors?.country?.message
                            : null
                        }
                        multiple={false}
                        material
                        disabled={formDisabled}
                        className={classes.field}
                      />
                    )}
                  />
                )}
                <Controller
                  name="city"
                  control={control}
                  rules={{
                    required: 'Required',
                    minLength: { value: 2, message: 'Invalid' },
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInput
                      type="text"
                      name="city"
                      title="City*"
                      placeholder={'City'}
                      onChange={(e) => {
                        onChange(e);
                        trigger('city' as keyof typeof initialValues);
                      }}
                      onBlur={(e) => {
                        trigger('city' as keyof typeof initialValues);
                        onBlur();
                      }}
                      value={!isEmpty(value) ? value : ''}
                      error={errors?.city?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                    />
                  )}
                />
              </WidgetSection>

              <WidgetSection
                title="Additional Details"
                narrowSpacing={true}
                classes={{
                  container: classes.section,
                }}
                useSeparator={false}
                style={{ margin: '-4px 0' }}
              >
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: false,
                    validate: (value) =>
                      checkEmail(value) || 'Invalid email format',
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInput
                      type="text"
                      name="email"
                      title="Email"
                      placeholder={'Email'}
                      onChange={(e) => {
                        trigger('email' as keyof typeof initialValues);
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        trigger('email' as keyof typeof initialValues);
                        onBlur();
                      }}
                      value={!isEmpty(value) ? value : ''}
                      error={errors?.email?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                    />
                  )}
                />

                <Controller
                  name="phoneType"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedChipInput
                      name={`phoneType`}
                      title="Phone Type*"
                      placeholder={'Phone Type'}
                      value={!isEmpty(value) ? value : ''}
                      onChange={(e) => {
                        onChange(e as string);
                        trigger(`phoneType` as keyof typeof initialValues);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        trigger('phoneType' as keyof typeof initialValues);
                      }}
                      selectOptions={lovs?.phoneTypes}
                      error={
                        errors.phoneType != undefined &&
                        !isEmpty(errors?.phoneType)
                          ? errors?.phoneType?.message
                          : null
                      }
                      multiple={false}
                      material
                      disabled={formDisabled}
                      className={classes.field}
                    />
                  )}
                />
                <Controller
                  name="street"
                  control={control}
                  rules={{
                    minLength: { value: 2, message: 'Invalid' },
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInput
                      type="text"
                      name="street"
                      title="Street"
                      placeholder={'Street'}
                      onChange={(e) => {
                        onChange(e);
                        trigger('street' as keyof typeof initialValues);
                      }}
                      onBlur={(e) => {
                        trigger('street' as keyof typeof initialValues);
                        onBlur();
                      }}
                      value={!isEmpty(value) ? value : ''}
                      error={errors?.street?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                    />
                  )}
                />
                <Controller
                  name="building"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <EnhancedInput
                      type="text"
                      name="building"
                      title="Building"
                      placeholder={'Building'}
                      onChange={(e) => {
                        trigger('building' as keyof typeof initialValues);
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        trigger('building' as keyof typeof initialValues);
                        onBlur();
                      }}
                      value={
                        !isEmpty(value) || value !== undefined ? value : null
                      }
                      error={errors?.building?.message}
                      className={classes.field}
                      material
                      disabled={formDisabled}
                    />
                  )}
                />
              </WidgetSection>
              <WidgetSection
                title="Product Details"
                narrowSpacing={true}
                classes={{ container: classes.sectionFullRow }}
                useSeparator={false}
                style={{ margin: '-4px 0' }}
              >
                {lovs != null &&
                  planDetailsFieldArray.fields.map((field, index) => {
                    const product = watchedPlanDetails[index]?.product;
                    const quoteName = watchedPlanDetails[index]?.name;
                    const selectedProducts = watchedPlanDetails
                      .map((pd) => pd.product)
                      .filter(Boolean) as string[];
                    const availableProducts = Object.fromEntries(
                      Object.entries(lovs.products).filter(
                        ([key]) =>
                          key === product || !selectedProducts.includes(key)
                      )
                    );

                    return (
                      <div className={classes.row} key={field.id}>
                        <Controller
                          name={
                            `planDetails.${index}.planId` as keyof typeof initialValues
                          }
                          control={control}
                          render={({ field: { value } }) => (
                            <EnhancedInput
                              type="text"
                              name={`planDetails.${index}.planId`}
                              title="Plan Id*"
                              value={(value as string) || ''}
                              onChange={() => undefined}
                              onBlur={() => undefined}
                              style={{ display: 'none', margin: '-10px 0' }}
                              customStyles={{
                                labelStyles: classes.repeaterField,
                              }}
                            />
                          )}
                        />

                        <EnhancedInput
                          type="text"
                          name={`planDetails.${index}.planId`}
                          placeholder="Quote Name"
                          title="Quote Name*"
                          value={quoteName || ''}
                          onChange={() => undefined}
                          onBlur={() => undefined}
                          material
                          disabled
                          style={{ margin: '-10px 0' }}
                          customStyles={{ labelStyles: classes.repeaterField }}
                        />

                        <Controller
                          name={
                            `planDetails.${index}.product` as keyof typeof initialValues
                          }
                          control={control}
                          rules={{
                            required: 'Required',
                            validate: (v) => checkQuoteName(v as string),
                          }}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <EnhancedChipInput
                              name={`planDetails.${index}.product`}
                              title="Product*"
                              placeholder="Product"
                              value={(value as string) || ''}
                              onChange={(e) => {
                                const selectedValue = e as string;
                                onChange(selectedValue);
                                trigger(
                                  `planDetails.${index}.product` as keyof typeof initialValues
                                );

                                planDetailsFieldArray.fields[index].product =
                                  selectedValue;
                                const updatedArray =
                                  planDetailsFieldArray.fields.map(
                                    (fld, i) => ({
                                      ...fld,
                                      ...watchedPlanDetails[i],
                                    })
                                  );
                                planDetailsProducts = updatedArray.map(
                                  (p) => p.product
                                );
                                handlePlanDetailsChange();

                                const newCounter = {
                                  ...planDetailsProductCounter,
                                  [selectedValue]:
                                    planDetailsProductCounter[
                                      selectedValue as keyof typeof initValuesPlanDetailsProductsCounter
                                    ] + 1,
                                };
                                setPlanDetailsProductCounter(newCounter);
                                validatePlateNumberOnProductChange(
                                  selectedValue,
                                  index
                                );

                                const newStatus = cloneDeep(
                                  planDetailsKeysStatus
                                );
                                newStatus[index] = {};
                                setPlanDetailsKeysStatus(newStatus);
                                clearInapplicablePlanDetailFields(
                                  index,
                                  selectedValue
                                );
                                logMotorDebug(
                                  'field-reset',
                                  'product onChange – sync hidden fields',
                                  {
                                    index,
                                    selectedValue,
                                    productTitle:
                                      lovs?.products?.[selectedValue],
                                  }
                                );
                                reviewTowingService();
                                reviewBreakdownServices();
                              }}
                              onBlur={() => {
                                const selectedValue =
                                  planDetailsFieldArray.fields[
                                    index
                                  ].product.toString();
                                validatePlateNumberOnProductChange(
                                  selectedValue,
                                  index
                                );
                                trigger(
                                  `planDetails.${index}.product` as keyof typeof initialValues
                                );

                                const newCounter = {
                                  ...planDetailsProductCounter,
                                  [selectedValue]:
                                    planDetailsProductCounter[
                                      selectedValue as keyof typeof initValuesPlanDetailsProductsCounter
                                    ] + 1,
                                };
                                setPlanDetailsProductCounter(newCounter);
                                validatePlateNumberOnProductChange(
                                  selectedValue,
                                  index
                                );

                                const newStatus = cloneDeep(
                                  planDetailsKeysStatus
                                );
                                newStatus[index] = {};
                                setPlanDetailsKeysStatus(newStatus);
                              }}
                              selectOptions={availableProducts}
                              material
                              error={
                                errors.planDetails &&
                                !isEmpty(errors.planDetails[index]?.product)
                                  ? errors.planDetails[index]?.product?.message
                                  : null
                              }
                              multiple={false}
                              disabled={formDisabled}
                              style={{ margin: '-10px 0' }}
                              customStyles={{
                                labelStyles: classes.repeaterField,
                              }}
                            />
                          )}
                        />

                        {isVisiblePlanDetailsField(
                          product,
                          'towingService'
                        ) && (
                          <Controller
                            name={
                              `planDetails.${index}.towingService` as keyof typeof initialValues
                            }
                            control={control}
                            rules={{ required: 'Required' }}
                            render={({
                              field: { onChange, onBlur, value },
                            }) => (
                              <EnhancedChipInput
                                name={`planDetails.${index}.towingService`}
                                title="Towing Service*"
                                placeholder="Towing Service"
                                value={(value as string) || ''}
                                onChange={(e) => {
                                  onChange(e as string);
                                  trigger(
                                    `planDetails.${index}.towingService` as keyof typeof initialValues
                                  );
                                }}
                                onBlur={onBlur}
                                selectOptions={towingService}
                                material
                                error={
                                  errors.planDetails &&
                                  !isEmpty(
                                    errors.planDetails[index]?.towingService
                                  )
                                    ? errors.planDetails[index]?.towingService
                                        ?.message
                                    : null
                                }
                                multiple={false}
                                disabled={formDisabled}
                                className={classes.field}
                                style={{ margin: '-10px 0' }}
                                customStyles={{
                                  labelStyles: classes.repeaterField,
                                }}
                              />
                            )}
                          />
                        )}

                        {isVisibleBreakdownServicesField(
                          product,
                          getValues('bodyType'),
                          getValues('weight'),
                          getValues('model'),
                          getValues('brand')
                        ) && (
                          <Controller
                            name={
                              `planDetails.${index}.breakdownServices` as keyof typeof initialValues
                            }
                            control={control}
                            rules={{ required: 'Required' }}
                            render={({
                              field: { onChange, onBlur, value },
                            }) => (
                              <EnhancedChipInput
                                name={`planDetails.${index}.breakdownServices`}
                                title="Mechanical/Electrical Breakdown Services*"
                                placeholder="Mechanical/Electrical Breakdown Services"
                                value={(value as string) || ''}
                                onChange={(e) => {
                                  onChange(e as string);
                                  trigger(
                                    `planDetails.${index}.breakdownServices` as keyof typeof initialValues
                                  );
                                }}
                                onBlur={onBlur}
                                selectOptions={breakdownServices}
                                material
                                error={
                                  errors.planDetails &&
                                  !isEmpty(
                                    errors.planDetails[index]
                                      ?.breakdownServices
                                  )
                                    ? errors.planDetails[index]
                                        ?.breakdownServices?.message
                                    : null
                                }
                                multiple={false}
                                disabled={formDisabled}
                                className={classes.breakdownServicesField}
                                style={{ margin: '-10px 0' }}
                                customStyles={{
                                  labelStyles: classes.breakdownServicesLabel,
                                }}
                              />
                            )}
                          />
                        )}

                        {isVisiblePlanDetailsField(product, 'vignette') && (
                          <>
                            <Controller
                              name={
                                `planDetails.${index}.vignetteCode` as keyof typeof initialValues
                              }
                              control={control}
                              rules={{ required: 'Required' }}
                              render={({
                                field: { onChange, onBlur, value },
                              }) => (
                                <EnhancedInput
                                  type="text"
                                  name={`planDetails.${index}.vignetteCode`}
                                  title="Vignette Code*"
                                  placeholder="Vignette Code"
                                  value={(value as string) || ''}
                                  onChange={(e) => {
                                    onChange(e);
                                    trigger(
                                      `planDetails.${index}.vignetteCode` as keyof typeof initialValues
                                    );
                                    planDetailsFieldArray.fields.forEach(
                                      (_, row) => validateVignetteOnChange(row)
                                    );
                                    setUsageTypes(
                                      adjustUsageTypesListAndReview()
                                    );
                                  }}
                                  onBlur={onBlur}
                                  error={
                                    errors.planDetails &&
                                    !isEmpty(
                                      errors.planDetails[index]?.vignetteCode
                                    )
                                      ? errors.planDetails[index]?.vignetteCode
                                          ?.message
                                      : null
                                  }
                                  disabled={formDisabled}
                                  material
                                  className={classes.field}
                                  style={{ margin: '-10px 0' }}
                                  customStyles={{
                                    labelStyles: classes.repeaterField,
                                  }}
                                />
                              )}
                            />
                            <Controller
                              name={
                                `planDetails.${index}.vignette` as keyof typeof initialValues
                              }
                              control={control}
                              rules={{ required: 'Required' }}
                              render={({
                                field: { onChange, onBlur, value },
                              }) => (
                                <EnhancedInput
                                  type="number"
                                  name={`planDetails.${index}.vignette`}
                                  title="Vignette*"
                                  placeholder="Vignette"
                                  value={(value as any) || ''}
                                  onChange={(e) => {
                                    onChange(e);
                                    trigger(
                                      `planDetails.${index}.vignette` as keyof typeof initialValues
                                    );
                                    planDetailsFieldArray.fields.forEach(
                                      (_, row) => validateVignetteOnChange(row)
                                    );
                                    setUsageTypes(
                                      adjustUsageTypesListAndReview()
                                    );
                                  }}
                                  onBlur={onBlur}
                                  error={
                                    errors.planDetails &&
                                    !isEmpty(
                                      errors.planDetails[index]?.vignette
                                    )
                                      ? errors.planDetails[index]?.vignette
                                          ?.message
                                      : null
                                  }
                                  disabled={formDisabled}
                                  material
                                  className={classes.field}
                                  style={{ margin: '-10px 0' }}
                                  customStyles={{
                                    labelStyles: classes.repeaterField,
                                  }}
                                />
                              )}
                            />
                          </>
                        )}

                        {isVisiblePlanDetailsField(product, 'coverageType') && (
                          <Controller
                            name={
                              `planDetails.${index}.coverageType` as keyof typeof initialValues
                            }
                            control={control}
                            rules={{ required: 'Required' }}
                            render={({
                              field: { onChange, onBlur, value },
                            }) => {
                              const conditions = cloneDeep(
                                planDetailsKeysStatus
                              );
                              return (
                                <EnhancedChipInput
                                  name={`planDetails.${index}.coverageType`}
                                  title="Plan*"
                                  value={(value as string) || ''}
                                  onChange={(e) => {
                                    onChange(e as string);
                                    conditions[index].coverageType =
                                      e === 'LUX_PLAN';
                                    setPlanDetailsKeysStatus(conditions);
                                  }}
                                  onBlur={onBlur}
                                  selectOptions={lovs.coverageTypes}
                                  material
                                  error={
                                    errors.planDetails &&
                                    !isEmpty(
                                      errors.planDetails[index]?.coverageType
                                    )
                                      ? errors.planDetails[index]?.coverageType
                                          ?.message
                                      : null
                                  }
                                  multiple={false}
                                  disabled={formDisabled}
                                  className={classes.field}
                                  style={{ margin: '-10px 0' }}
                                  customStyles={{
                                    labelStyles: classes.repeaterField,
                                  }}
                                />
                              );
                            }}
                          />
                        )}

                        {isVisiblePlanDetailsField(product, 'carValue') && (
                          <Controller
                            name={
                              `planDetails.${index}.carValue` as keyof typeof initialValues
                            }
                            control={control}
                            render={({
                              field: { onChange, onBlur, value },
                            }) => (
                              <EnhancedCurrencyInput
                                type="number"
                                name={`planDetails.${index}.carValue`}
                                title="Car Value*"
                                placeholder="Car Value"
                                onChange={(e) => {
                                  const num = Number(e.target.value);
                                  onChange(num);
                                  trigger(
                                    `planDetails.${index}.carValue` as keyof typeof initialValues
                                  );
                                  const list = adjustAgencyRepair(
                                    num,
                                    lovs['agencyRepairs']
                                  );
                                  setAgencyRepairs((ar) => ({
                                    ...ar,
                                    [index]: list,
                                  }));
                                }}
                                onBlur={onBlur}
                                value={(value as any) || ''}
                                currencyTitle="$"
                                currencyIcon={`${CDN_URL}/icons/dollar-primary.svg`}
                                material
                                disabled={formDisabled}
                                className={classes.field}
                                style={{ margin: '-10px 0' }}
                                customStyles={{
                                  labelStyles: classes.repeaterField,
                                }}
                              />
                            )}
                          />
                        )}

                        {isVisiblePlanDetailsField(
                          product,
                          'includeAgencyRepair'
                        ) &&
                          getValues('usageType') === 'Private' &&
                          getValues('yearOfMake') >= 2022 &&
                          getValues('yearOfMake') <= 2025 &&
                          planDetailsKeysStatus[index]?.coverageType && (
                            <Controller
                              name={
                                `planDetails.${index}.includeAgencyRepair` as keyof typeof initialValues
                              }
                              control={control}
                              rules={{ required: 'Required' }}
                              render={({
                                field: { onChange, onBlur, value },
                              }) => (
                                <EnhancedChipInput
                                  name={`planDetails.${index}.includeAgencyRepair`}
                                  title="Include Agency Repair*"
                                  placeholder="Include Agency Repair"
                                  value={(value as string) || ''}
                                  onChange={(e) => {
                                    onChange(e as string);
                                    trigger(
                                      `planDetails.${index}.includeAgencyRepair` as keyof typeof initialValues
                                    );
                                  }}
                                  onBlur={onBlur}
                                  selectOptions={agencyRepairs[index]}
                                  material
                                  error={
                                    errors.planDetails &&
                                    !isEmpty(
                                      errors.planDetails[index]
                                        ?.includeAgencyRepair
                                    )
                                      ? errors.planDetails[index]
                                          ?.includeAgencyRepair?.message
                                      : null
                                  }
                                  multiple={false}
                                  disabled={formDisabled}
                                  className={classes.field}
                                  style={{ margin: '-10px 0' }}
                                  customStyles={{
                                    labelStyles: classes.repeaterField,
                                  }}
                                />
                              )}
                            />
                          )}

                        <div
                          className="btn-box"
                          style={{
                            marginTop: 12,
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'row',
                          }}
                        >
                          {!formDisabled &&
                            planDetailsFieldArray.fields.length - 1 === index &&
                            planDetailsFieldArray.fields.length <
                              Object.keys(lovs.products).length && (
                              <span
                                style={{
                                  paddingRight: 10,
                                  color: MAIN_THEME.color.black,
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  planDetailsFieldArray.append({
                                    ...initialValues.planDetails[0],
                                  });
                                  const newStatus = cloneDeep(
                                    planDetailsKeysStatus
                                  );
                                  newStatus.push({});
                                  setPlanDetailsKeysStatus(newStatus);
                                }}
                              >
                                <AddCircleOutlineIcon fontSize="small" />
                              </span>
                            )}
                          {!formDisabled &&
                            planDetailsFieldArray.fields.length > 1 && (
                              <span
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={() => {
                                  planDetailsFieldArray.remove(index);
                                  const newStatus = cloneDeep(
                                    planDetailsKeysStatus
                                  );
                                  newStatus.splice(index, 1);
                                  setPlanDetailsKeysStatus(newStatus);
                                }}
                              >
                                <HighlightOffIcon fontSize="small" />
                              </span>
                            )}
                        </div>
                      </div>
                    );
                  })}
              </WidgetSection>

              {!isClosed && isEnabled && buttonLabel === 'Calculate' && (
                <div
                  style={{
                    width: '100%',
                    justifyContent: 'flex-end',
                    display: 'flex',
                    padding: '15px 0 0',
                  }}
                >
                  <EnhancedButton
                    type={buttonType}
                    state={submitButtonState}
                    backgroundColor={MAIN_THEME.color.black}
                    color={MAIN_THEME.color.white}
                    style={{ display: 'inline' }}
                    onClick={
                      buttonType === 'button'
                        ? () => {
                            logMotorDebug(
                              'calculate',
                              'Calculate clicked – validation blocking submit',
                              { errorKeys: Object.keys(errors), errors }
                            );
                            void handleSubmit(onSubmit)();
                          }
                        : undefined
                    }
                  >
                    {buttonLabel}
                  </EnhancedButton>
                </div>
              )}
            </WidgetPaper>
          </form>

          {newAppDrawerOpen && (
            <ApplicationDrawer
              open={newAppDrawerOpen}
              onClose={() => setNewAppDrawerOpen(false)}
              onSuccess={() => {
                setNewAppDrawerOpen(false);
              }}
            />
          )}
          {newApplicationWithMultipleAgencieDrawerOpen && (
            <ApplicationDrawerWithMultipleAgencies
              open={newApplicationWithMultipleAgencieDrawerOpen}
              loggedInAgentId={user.info.agentId}
              loggedInuserFirstName={user.info.firstName}
              loggedInuserLastName={user.info.lastName}
              lineOfBusiness={'Motor'}
              agentType={agentType}
              onClose={() =>
                setNewApplicationWithMultipleAgencieDrawerOpen(false)
              }
              onSuccess={() => undefined}
            />
          )}
        </div>
      </>
    );
  };

  const renderLeftSection = () => {
    return (
      <>
        <EntityInfoWidget
          background="linear-gradient(98.3deg, rgb(0, 0, 0) 10.6%, rgb(255, 0, 0) 97.7%)"
          classification={{ classifications: {}, value: '' }}
          progress={getApplicationProgress(
            entityResult?.data?.Insurance?.entities?.application?.views
              ?.Insurance_all?.properties?.Status
          )}
          supportsClassification={true}
          supportsProgressBar={true}
          title={applicationInfo.applicationName}
          view={updatedLeftSideInfo}
        ></EntityInfoWidget>
      </>
    );
  };
  //#endregion

  return loadingState ? (
    <Loader />
  ) : (
    <>
      <div>
        {copyPolicyDetailsDrawerOpen && (
          <ApplicationCopyPolicyDetailsDrawer
            open={copyPolicyDetailsDrawerOpen}
            onClose={() => setCopyPolicyDetailsDrawerOpen(false)}
            onSuccess={(policyNumber: string) => {
              getCopyPolicyDetails(policyNumber);
            }}
          />
        )}
      </div>
      <StaticLayout
        name={'Application Details'}
        leftChildren={renderLeftSection()}
        mainChildren={renderMainChildren()}
      />
    </>
  );
};

export default ApplicationDetailsPageMotor;
